// src/components/dashboard/LandingPage.jsx
import React, { useState, useEffect, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import { 
  Activity, Droplets, Heart, Brain, Moon, Trophy, Target, 
  Sparkles, Coffee, Sun, Sunset, Shield,
  Flame, AlertCircle,
  ChevronRight, Plus, Camera, Utensils, Pill, Thermometer,
  Smartphone, RefreshCw,
  Wifi, WifiOff, AlertTriangle, Info,
  FileText, Award, TrendingUp
} from 'lucide-react';
import { 
  LineChart, Line, Area, RadialBarChart, RadialBar, 
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart as RePieChart, Pie, Cell, ComposedChart
} from 'recharts';
import { useAuth } from '../../hooks/useAuth';
import { useMediCureOnData } from '../../hooks/useMediCureOnData';
import MediCureOnSidebar from '../common/MediCureOnSidebar';
import MediCureOnHeader from '../common/MediCureOnHeader';
import useIntelligentHealthData from '../../hooks/useIntelligentHealthData';

// Import API configuration
import { apiConfig } from '../../config/apiConfig';

// Import health calculator utilities
import { HealthScoreCalculator } from '../../utils/healthScoreCalculator';
import { HealthDashboardProcessor } from '../../utils/healthDashboardProcessor';

// OPTIMIZATION: Advanced device detection and performance configuration
const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /iPad|iPhone|iPod|Android/i.test(userAgent);
  const isTablet = /iPad/i.test(userAgent) || (window.innerWidth >= 768 && window.innerWidth <= 1024);
  const isDesktop = !isMobile && window.innerWidth > 1024;
  
  return {
    isMobile,
    isTablet: isMobile || isTablet,
    isDesktop,
    screenWidth: window.innerWidth,
    hasTouch: 'ontouchstart' in window
  };
};

// OPTIMIZATION: Device-aware performance configuration
const getPerformanceConfig = () => {
  const device = getDeviceType();
  
  if (device.isDesktop) {
    return {
      refreshInterval: 30000, // 30s for desktop (fast)
      apiTimeout: 8000, // 8s timeout
      debounceDelay: 100, // Quick debounce
      batchSize: 10, // Larger batches
      enableParallelFetch: true, // Enable parallel fetching
      memoTTL: 30000, // 30s memo cache
      maxConcurrentRequests: 6
    };
  } else if (device.isTablet) {
    return {
      refreshInterval: 120000, // 2 minutes for tablets (balanced)
      apiTimeout: 12000, // 12s timeout
      debounceDelay: 300, // Moderate debounce
      batchSize: 5, // Smaller batches
      enableParallelFetch: true, // Enable parallel fetching but limited
      memoTTL: 120000, // 2 minute memo cache
      maxConcurrentRequests: 3
    };
  } else {
    return {
      refreshInterval: 300000, // 5 minutes for mobile (conservative)
      apiTimeout: 15000, // 15s timeout
      debounceDelay: 500, // Heavy debounce
      batchSize: 3, // Small batches
      enableParallelFetch: false, // Disable parallel fetching
      memoTTL: 300000, // 5 minute memo cache
      maxConcurrentRequests: 2
    };
  }
};

// OPTIMIZATION: Memoized component for dynamic performance optimization
const LandingPage = memo(({ onNavigate }) => {
  const { user, userInfo, logout, isLoading } = useAuth();
  const { 
    profilePicture, 
    getUserDisplayName,
    getGreeting,
    userSubscription,
    isLoading: dataLoading,
    refreshAll
  } = useMediCureOnData();

  // State declarations first
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [currentDeviceType, setCurrentDeviceType] = useState(null);

  // OPTIMIZATION: Get device type info
  const deviceType = useMemo(() => getDeviceType(), []);

  // OPTIMIZATION: Get device-aware performance config
  const performanceConfig = useMemo(() => {
    const config = getPerformanceConfig();
    const deviceTypeInfo = getDeviceType();
    
    // Log device type change only when it actually changes
    if (currentDeviceType !== deviceTypeInfo.type) {
      setCurrentDeviceType(deviceTypeInfo.type);
      console.log(`🚀 MediCureOn Performance: Optimized for ${deviceTypeInfo.type}`, {
        refreshInterval: `${config.refreshInterval/1000}s`,
        apiTimeout: `${config.apiTimeout/1000}s`,
        parallelFetch: config.enableParallelFetch,
        maxConcurrent: config.maxConcurrentRequests
      });
    }
    
    return config;
  }, [currentDeviceType]);
  
  console.log('🚀 PERFORMANCE CONFIG:', {
    device: deviceType.isDesktop ? 'Desktop' : deviceType.isTablet ? 'Tablet' : 'Mobile',
    config: performanceConfig
  });

  // Health Trends timeRange state - moved to main component level for hook integration
  const [healthTrendsTimeRange, setHealthTrendsTimeRange] = useState('7D');

  // OPTIMIZATION: Device-aware data fetching configuration
  const daysToFetch = useMemo(() => {
    const daysMap = {
      '7D': 7,
      '30D': 30,
      '60D': 60,
      '90D': 90,
      // Support alternative formats that might be used by components
      '7days': 7,
      '30days': 30,
      '60days': 60,
      '90days': 90
    };
    const days = daysMap[healthTrendsTimeRange] || 7;
    
    // Optimize data fetch amount based on device capability
    const optimizedDays = deviceType.isDesktop ? days : Math.min(days, 7);
    
    console.log(`🎯 TIMERANGE CHANGE: ${healthTrendsTimeRange} → ${optimizedDays} days (device optimized from ${days})`);
    console.log(`📱 DEVICE TYPE: ${deviceType.isDesktop ? 'Desktop' : deviceType.isTablet ? 'Tablet' : 'Mobile'}`);
    console.log(`🎯 AVAILABLE MAPPINGS:`, Object.keys(daysMap));
    return optimizedDays;
  }, [healthTrendsTimeRange, deviceType.isDesktop, deviceType.isTablet]);

  const { 
    healthData, 
    isLoading: healthDataLoading, 
    error: healthDataError, 
    refreshData: refreshHealthData,
    deviceStatus,
    lastSync,
    dataAvailability,
    hasAnyData
  } = useIntelligentHealthData(daysToFetch);

  // Challenge data state - similar to OverviewTab
  const [userActiveChallenges, setUserActiveChallenges] = useState([]);
  const [challengeProgress, setChallengeProgress] = useState({});
  const [challengesLoading, setChallengesLoading] = useState(true);

  // Health score calculation state
  const [healthScore, setHealthScore] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  
  // Initialize health calculators
  const healthCalculator = React.useMemo(() => new HealthScoreCalculator(), []);
  const dashboardProcessor = React.useMemo(() => new HealthDashboardProcessor(), []);

  useEffect(() => {
  if (!healthDataLoading) {
    // Health data is ready for processing
  }
}, [healthData, healthDataLoading]);

  // OPTIMIZATION: Memoize heavy health score calculations for iPad performance
  const calculatedHealthScore = useMemo(() => {
    if (!healthData || healthDataLoading) return null;
    
    try {
      const realOverallScore = healthData?.score?.overall || 0;
      return {
        overallScore: realOverallScore,
        level: realOverallScore >= 80 ? 'Good' : realOverallScore >= 60 ? 'Fair' : 'Needs Attention',
        breakdown: Object.entries(healthData?.score?.components || {}).map(([key, value]) => ({
          category: key,
          score: value || 0
        }))
      };
    } catch (error) {
      console.error('❌ Error calculating health score:', error);
      return null;
    }
  }, [healthData, healthDataLoading]);

  // OPTIMIZATION: Device-aware health data processing with performance-based memoization
  const processedHealthData = useMemo(() => {
    if (!healthData || healthDataLoading) return null;
    
    // Skip heavy processing on mobile/tablet devices until health score is ready
    if (!deviceType.isDesktop && !calculatedHealthScore) {
      console.log('📱 MOBILE: Skipping heavy processing until health score ready');
      return null;
    }
    
    const startTime = performance.now();
    try {
      const result = dashboardProcessor.processHealthDataForDashboard(healthData);
      const processingTime = performance.now() - startTime;
      console.log(`⚡ PROCESSING: ${processingTime.toFixed(2)}ms (${deviceType.isDesktop ? 'Desktop' : 'Mobile'})`);
      return result;
    } catch (error) {
      console.error('❌ Error processing health data:', error);
      return null;
    }
  }, [healthData, healthDataLoading, dashboardProcessor, calculatedHealthScore, deviceType.isDesktop]);

  // Update states only when calculations are complete
  useEffect(() => {
    if (calculatedHealthScore) {
      setHealthScore(calculatedHealthScore);
    }
    if (processedHealthData) {
      setProcessedData(processedHealthData);
    }
  }, [calculatedHealthScore, processedHealthData]);

  // Add debugging for medications
  useEffect(() => {
    if (healthData) {
      const sleepValue = typeof healthData?.realTimeMetrics?.sleep === 'object' ? healthData?.realTimeMetrics?.sleep?.value : healthData?.realTimeMetrics?.sleep;
      const sleepConverted = sleepValue && sleepValue > 24 ? Number((sleepValue / 60).toFixed(1)) : sleepValue;
      
      const medicationsData = healthData?.medications;
      let medicationsCount = 0;
      if (medicationsData) {
        if (Array.isArray(medicationsData.currentMedications)) medicationsCount += medicationsData.currentMedications.length;
        if (medicationsData.supplements?.vitaminsSupplements) medicationsCount += medicationsData.supplements.vitaminsSupplements.filter(item => item && item.trim() !== '' && item.trim() !== ',').length;
        if (medicationsData.supplements?.herbalRemedies) medicationsCount += medicationsData.supplements.herbalRemedies.filter(item => item && item.trim() !== '' && item.trim() !== ',').length;
      }
      
      console.log('🚀 FIXED HEALTH DATA: Medications:', medicationsCount, 'Sleep:', sleepConverted, 'h (converted from', sleepValue, ')');
      console.log('🚀 SLEEP DEBUG: Raw sleep value:', sleepValue, '→ Converted:', sleepConverted);
      console.log('🚀 MEDICATIONS DEBUG: Structure:', medicationsData, '→ Count:', medicationsCount);
    }
  }, [healthData]);

  // OPTIMIZATION: Dynamic timer frequency based on device performance
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, performanceConfig.refreshInterval);
    return () => clearInterval(timer);
  }, [performanceConfig.refreshInterval]);

  // Challenge data loading function - load from backend database first
  // OPTIMIZATION: Debounced challenge loading for iPad performance
  const loadChallengeData = useCallback(async () => {
    if (!user?.localAccountId && !user?.username && !userInfo?.localAccountId) {
      setChallengesLoading(false);
      return;
    }

    try {
      setChallengesLoading(true);
      const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
      const userCountry = user?.country || userInfo?.country || 'us';
      
      // OPTIMIZATION: Device-aware request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), performanceConfig.apiTimeout);
      
        // Try backend first - use same API as ChallengesTab
        const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.idToken}`,
            'X-User-Country': userCountry
          },
          body: JSON.stringify({
            action: 'get',
            userId: userId,
            country: userCountry
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        
        const challengeData = result.data?.challengeData || {};
        
        // Use backend data
        setUserActiveChallenges(challengeData.activeChallenges || []);
        setChallengeProgress(challengeData.challengeProgress || {});
        setChallengesLoading(false);
        return;
      } else {
        console.warn('⚠️ LandingPage: Backend failed, falling back to localStorage');
      }
    } catch (error) {
      console.error('❌ LandingPage: Backend error, falling back to localStorage:', error);
    }

    // Fallback to localStorage if backend fails
    try {
      const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
      
      // Use the same localStorage keys as ChallengesTab
      const challengeKey = `challenges_${userId}`;
      const fallbackKey = `challenges_fallback_${userId}`;
      
      // Try main storage first, then fallback
      let storedData = localStorage.getItem(challengeKey);
      let source = 'main';
      
      if (!storedData) {
        storedData = localStorage.getItem(fallbackKey);
        source = 'fallback';
      }
      
      if (storedData) {
        const parsed = JSON.parse(storedData);
        
        // Handle different data structures from main vs fallback storage
        if (source === 'fallback') {
          setUserActiveChallenges(parsed.activeChallenges || []);
          setChallengeProgress(parsed.challengeProgress || {});
        } else {
          setUserActiveChallenges(parsed.userActiveChallenges || parsed.activeChallenges || []);
          setChallengeProgress(parsed.challengeProgress || {});
        }
      } else {
        setUserActiveChallenges([]);
        setChallengeProgress({});
      }
    } catch (error) {
      console.error('❌ Error loading challenge data:', error);
      setUserActiveChallenges([]);
      setChallengeProgress({});
    } finally {
      setChallengesLoading(false);
    }
  }, [user, userInfo, performanceConfig]);

  // OPTIMIZATION: Device-aware refresh with parallel loading for desktop
  const handleRefresh = useCallback(async () => {
    console.log('🔄 REFRESH: Starting refresh with config:', performanceConfig);
    
    if (performanceConfig.enableParallelFetch) {
      // Desktop: Parallel refresh for better performance
      console.log('🚀 DESKTOP: Using parallel refresh');
      await Promise.all([
        refreshAll(),
        refreshHealthData(),
        loadChallengeData() // Load challenges in parallel
      ]);
    } else {
      // Mobile/Tablet: Sequential refresh to avoid overwhelming device
      console.log('📱 MOBILE: Using sequential refresh');
      await refreshAll();
      await refreshHealthData();
      await loadChallengeData();
    }
  }, [refreshAll, refreshHealthData, loadChallengeData, performanceConfig]);

  const getTimeBasedContent = () => {
    const hour = currentTime.getHours();
    let message = '';
    let icon = null;
    
    if (hour >= 5 && hour < 12) {
      icon = <Coffee className="w-4 h-4" />;
      message = hour < 8 ? 'Rise and shine! Start your day right' : 'Good morning! Stay energized';
    } else if (hour >= 12 && hour < 17) {
      icon = <Sun className="w-4 h-4" />;
      message = 'Keep up the great work today';
    } else if (hour >= 17 && hour < 21) {
      icon = <Sunset className="w-4 h-4" />;
      message = 'Evening check-in time';
    } else {
      icon = <Moon className="w-4 h-4" />;
      message = 'Rest well for tomorrow';
    }
    
    return { message, icon };
  };

  const { message, icon: timeIcon } = getTimeBasedContent();


  // Helper functions for challenge data - same as OverviewTab
  const getActiveChallengeNames = useCallback(() => {
    if (!userActiveChallenges || userActiveChallenges.length === 0) {
      return [];
    }
    return userActiveChallenges.map(challenge => 
      challenge.challengeName || challenge.name || challenge.challengeId || 'Unknown Challenge'
    );
  }, [userActiveChallenges]);

  const getActiveChallengeDetails = useCallback(() => {
    if (!userActiveChallenges || userActiveChallenges.length === 0) {
      return [];
    }
    return userActiveChallenges.map(challenge => {
      // Map challenge names to their known difficulties from backend
      const challengeDifficultyMap = {
        'Step Starter': 'easy',
        'Low Sugar Champion': 'easy',
        'Step Streak': 'medium',
        'Century Step Master': 'hard',
        'BP Logger': 'medium',
        'Gentle Morning Walker': 'easy',
        '10,000 Steps Daily': 'medium',
        'Hydration Master': 'easy',
        'Sleep Champion': 'medium',
        'Cardio Warrior': 'hard',
        'Mindful Walker': 'easy',
        'Consistency King': 'expert'
      };
      
      const challengeName = challenge.challengeName || challenge.name || challenge.challengeId || 'Unknown Challenge';
      const difficulty = challenge.difficulty || challengeDifficultyMap[challengeName] || 'medium';
      
      return {
        id: challenge.challengeId || challenge.id,
        name: challengeName,
        icon: challenge.icon || '🏃‍♂️',
        difficulty: difficulty,
        status: challenge.status || 'active'
      };
    });
  }, [userActiveChallenges]);
  
  useEffect(() => {
    // Load on mount
    loadChallengeData();
    
    // Listen for challenge data updates from ChallengesTab
    const handleChallengeDataUpdate = (event) => {
      if (event.detail.userActiveChallenges) {
        setUserActiveChallenges(event.detail.userActiveChallenges);
      }
      if (event.detail.challengeProgress) {
        setChallengeProgress(event.detail.challengeProgress);
      }
    };
    
    window.addEventListener('challengeDataUpdated', handleChallengeDataUpdate);
    
    return () => {
      window.removeEventListener('challengeDataUpdated', handleChallengeDataUpdate);
    };
  }, [user?.localAccountId, user?.username, userInfo?.localAccountId, loadChallengeData]);

  // Helper function to calculate water glasses from ml  
  const getWaterGlasses = () => {
    if (processedHealthData?.waterGlasses) {
      return processedHealthData.waterGlasses;
    }
    
    // ✅ FIXED: Use correct path from useIntelligentHealthData hook
    const waterGlasses = healthData?.wellness?.hydration?.glasses || 0;
    const waterMl = healthData?.wellness?.hydration?.ml || 0;
    
    // If we have glasses count, use it; otherwise calculate from ml
    if (waterGlasses > 0) {
      return waterGlasses;
    }
    
    // Fallback calculation: 250ml = 1 glass (matching our hook)
    return Math.round(waterMl / 250);
  };

  if (isLoading || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Loading Your Health Dashboard</h2>
          <p className="text-gray-600">Preparing your personalized insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <MediCureOnSidebar onNavigate={onNavigate} activePage="landing" />
      
      <div className="flex-1 overflow-auto">
        <MediCureOnHeader
          subtitle={<div className="flex items-center space-x-2"><span>{message}</span>{timeIcon}</div>}
          showRefresh={true}
          onRefresh={handleRefresh}
        />

        <div className="p-6">
          {/* Enhanced Hero Health Score Section */}
          <div className="mb-8">
            <ModernHealthScoreHero 
              healthData={healthData}
              healthScore={healthScore}
              processedHealthData={processedHealthData}
              profileData={null}
              deviceStatus={deviceStatus}
              lastSync={lastSync}
              dataAvailability={dataAvailability}
              hasAnyData={hasAnyData}
              dataLoading={healthDataLoading}
              dataError={healthDataError}
              onRefresh={refreshHealthData}
            />
          </div>

          {/* Enhanced Tab Navigation */}
          <div className="mb-6">
            <div className="flex p-1 space-x-1 bg-white border border-gray-200 shadow-sm rounded-xl">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'nutrition', label: 'Nutrition', icon: Utensils }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all flex-1 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#02276F] to-blue-700 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <ModernOverviewTab 
              healthData={healthData}
              onNavigate={onNavigate}
              profileData={null}
              user={user}
              dataAvailability={dataAvailability}
              userActiveChallenges={userActiveChallenges}
              challengeProgress={challengeProgress}
              challengesLoading={challengesLoading}
              getActiveChallengeDetails={getActiveChallengeDetails}
              getActiveChallengeNames={getActiveChallengeNames}
              healthTrendsTimeRange={healthTrendsTimeRange}
              setHealthTrendsTimeRange={setHealthTrendsTimeRange}
            />
          )}
          {activeTab === 'nutrition' && (
            <ModernNutritionTab 
              healthData={healthData}
              dataAvailability={dataAvailability}
              onNavigate={onNavigate}
            />
          )}

          {/* 💰 MEDICOIN (MCN) - CURRENCY OF THE FUTURE SHOWCASE */}
          <div className="p-6 mb-8 border-2 border-yellow-300 shadow-2xl bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 rounded-2xl backdrop-blur-sm">
            <div className="relative overflow-hidden">
              {/* Animated Background Elements */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute w-32 h-32 bg-yellow-400 rounded-full animate-pulse top-4 right-4"></div>
                <div className="absolute w-20 h-20 bg-orange-400 rounded-full animate-bounce bottom-4 left-4"></div>
              </div>
              
              <div className="relative z-10">
                {/* Header with Animated Badges */}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl animate-bounce">💰</span>
                    <h2 className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text">
                      MediCoin (MCN) - Currency of the Future
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-green-500 to-green-600 animate-pulse">
                      ✨ UTILITY TOKEN
                    </span>
                    <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-purple-500 to-purple-600 animate-pulse">
                      🚀 ICO LIVE
                    </span>
                    <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-blue-600 animate-pulse">
                      💎 6-132% STAKING
                    </span>
                  </div>
                </div>

                {/* Multi-Purpose Currency Grid */}
                <div className="grid gap-4 mb-6 md:grid-cols-2 lg:grid-cols-4">
                  <div className="p-4 transition-all duration-300 bg-white border-2 border-yellow-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                    <div className="text-center">
                      <span className="block mb-2 text-3xl">🎯</span>
                      <h3 className="mb-2 font-bold text-yellow-700">Challenge Rewards</h3>
                      <p className="text-xs text-gray-600">Earn MCN tokens for completing health challenges</p>
                    </div>
                  </div>
                  
                  <div className="p-4 transition-all duration-300 bg-white border-2 border-orange-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                    <div className="text-center">
                      <span className="block mb-2 text-3xl">🔄</span>
                      <h3 className="mb-2 font-bold text-orange-700">Points Conversion</h3>
                      <p className="text-xs text-gray-600">Convert activity points to MediCoin instantly</p>
                    </div>
                  </div>
                  
                  <div className="p-4 transition-all duration-300 bg-white border-2 border-purple-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                    <div className="text-center">
                      <span className="block mb-2 text-3xl">💳</span>
                      <h3 className="mb-2 font-bold text-purple-700">Subscription-Based Discounts</h3>
                      <p className="text-xs text-gray-600">5-30% discounts on services based on your tier level</p>
                    </div>
                  </div>
                  
                  <div className="p-4 transition-all duration-300 bg-white border-2 border-green-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                    <div className="text-center">
                      <span className="block mb-2 text-3xl">🏥</span>
                      <h3 className="mb-2 font-bold text-green-700">Healthcare Payments</h3>
                      <p className="text-xs text-gray-600">Pay for medical services with MediCoin</p>
                    </div>
                  </div>
                </div>

                {/* Value Proposition Highlights */}
                <div className="grid gap-4 mb-6 md:grid-cols-3">
                  <div className="p-4 border border-yellow-300 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl">
                    <h4 className="mb-2 font-bold text-yellow-800">💰 Revolutionary Economics</h4>
                    <ul className="space-y-1 text-xs text-yellow-700">
                      <li>• Deflationary token model</li>
                      <li>• Burn mechanism reduces supply</li>
                      <li>• Staking rewards up to 132% APY</li>
                      <li>• Limited total supply</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border border-purple-300 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl">
                    <h4 className="mb-2 font-bold text-purple-800">🌐 Global Ecosystem</h4>
                    <ul className="space-y-1 text-xs text-purple-700">
                      <li>• Healthcare provider network</li>
                      <li>• Pharmaceutical partnerships</li>
                      <li>• Insurance integrations</li>
                      <li>• Global marketplace access</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border border-green-300 bg-gradient-to-br from-green-100 to-teal-100 rounded-xl">
                    <h4 className="mb-2 font-bold text-green-800">🚀 Future Technology</h4>
                    <ul className="space-y-1 text-xs text-green-700">
                      <li>• AI-powered health insights</li>
                      <li>• Blockchain security</li>
                      <li>• Cross-chain compatibility</li>
                      <li>• Smart contract automation</li>
                    </ul>
                  </div>
                </div>

                {/* ICO Investment Opportunity */}
                <div className="p-6 mb-4 text-white bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 rounded-2xl">
                  <div className="text-center">
                    <h3 className="mb-2 text-xl font-bold">🔥 LIMITED TIME ICO OPPORTUNITY</h3>
                    <p className="mb-4 text-sm opacity-90">
                      Join the healthcare revolution! MediCoin ICO is LIVE with exclusive early investor benefits.
                    </p>
                    <div className="grid gap-3 mb-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                        <p className="text-xs opacity-80">1 Year Staking</p>
                        <p className="font-bold">6% APY</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                        <p className="text-xs opacity-80">2 Year Staking</p>
                        <p className="font-bold">24% APY</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                        <p className="text-xs opacity-80">3 Year Staking</p>
                        <p className="font-bold">84% APY</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                        <p className="text-xs opacity-80">5 Year Staking</p>
                        <p className="font-bold text-yellow-300">132% APY</p>
                      </div>
                    </div>
                    <a 
                      href="https://ico.medicureon.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block px-8 py-3 font-bold text-purple-600 transition-all duration-300 transform bg-white rounded-full shadow-lg hover:bg-yellow-100 hover:scale-105"
                    >
                      🚀 INVEST IN MEDICOIN NOW - ICO.MEDICUREON.COM
                    </a>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="p-4 text-center bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl">
                  <p className="font-bold text-white">
                    💎 MediCoin: The only cryptocurrency designed specifically for healthcare. 
                    <span className="block mt-1 text-sm opacity-90">
                      Earn rewards, save money, invest in the future of medicine!
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 🚀 PHASE 2 ROADMAP - Comprehensive IoMT Integration Showcase */}
          <div className="p-6 mb-6 border-2 border-purple-400 rounded-xl bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900">
            <div className="flex items-start gap-4">
              <span className="text-3xl">🚀</span>
              <div className="flex-1">
                <h3 className="mb-3 text-xl font-bold text-white">Phase 2: Revolutionary IoMT Ecosystem Expansion</h3>
                <p className="mb-4 text-sm text-purple-100">
                  <span className="font-semibold">Coming Soon:</span> MediCureOn will integrate with the world's most comprehensive IoMT ecosystem - 
                  from consumer wearables to clinical-grade equipment, voice assistants to hospital systems.
                </p>
                
                {/* Device Categories Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {/* Consumer Devices */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-green-800/40 to-green-600/40 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">⌚</span>
                      <h4 className="font-semibold text-green-200">Consumer Wearables</h4>
                    </div>
                    <div className="space-y-1 text-xs text-green-100">
                      <p>✅ Apple Watch Series 9/10/Ultra</p>
                      <p>✅ Samsung Galaxy Watch 5-7</p>
                      <p>✅ Fitbit Versa/Sense/Charge</p>
                      <p>✅ Garmin Venu/Fenix/Forerunner</p>
                      <p>✅ Withings Complete Ecosystem</p>
                      <p>✅ Oura Ring Gen 3/4</p>
                      <p>🔜 WHOOP, Polar, Suunto</p>
                      <p>🔜 Xiaomi Mi Band, Huawei</p>
                    </div>
                  </div>

                  {/* Clinical Devices */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-blue-800/40 to-blue-600/40 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🏥</span>
                      <h4 className="font-semibold text-blue-200">Clinical Grade</h4>
                    </div>
                    <div className="space-y-1 text-xs text-blue-100">
                      <p>✅ AliveCor KardiaMobile ECG</p>
                      <p>✅ Omron HeartGuide BP</p>
                      <p>🔜 Dexcom CGM Integration</p>
                      <p>🔜 Philips Clinical Equipment</p>
                      <p>🔜 Omron Professional BPMs</p>
                      <p>🔜 ResMed Sleep Devices</p>
                      <p>🔜 VoCare Vitals360</p>
                      <p>🔜 FreeStyle Libre CGM</p>
                    </div>
                  </div>

                  {/* Emerging Tech */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-purple-800/40 to-purple-600/40 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🔬</span>
                      <h4 className="font-semibold text-purple-200">Emerging Tech</h4>
                    </div>
                    <div className="space-y-1 text-xs text-purple-100">
                      <p>🔜 K'Watch Non-invasive Glucose</p>
                      <p>🔜 Smart Clinical Glasses</p>
                      <p>🔜 Smart Contact Lenses</p>
                      <p>🔜 Smart Bandages</p>
                      <p>🔜 Smart Pills/Sensors</p>
                      <p>🔜 Smart Mirrors</p>
                      <p>🔜 Posture Monitors</p>
                      <p>🔜 Smart Inhalers</p>
                    </div>
                  </div>

                  {/* Voice & Smart Home */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-800/40 to-orange-600/40 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">🎤</span>
                      <h4 className="font-semibold text-yellow-200">Voice & Smart Home</h4>
                    </div>
                    <div className="space-y-1 text-xs text-yellow-100">
                      <p>🔜 Amazon Alexa Integration</p>
                      <p>🔜 Google Assistant</p>
                      <p>🔜 Apple Siri/HomePod</p>
                      <p>🔜 Samsung Bixby</p>
                      <p>🔜 Voice Health Scanners</p>
                      <p>🔜 Smart Health Hubs</p>
                      <p>🔜 Voice Medication Dispensers</p>
                      <p>🔜 Smart Speaker Health Monitoring</p>
                    </div>
                  </div>
                </div>

                {/* Phase 2 Highlights */}
                <div className="grid gap-4 mt-6 md:grid-cols-3">
                  <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🌟</span>
                      <h5 className="font-semibold text-white">10+ New Integrations</h5>
                    </div>
                    <p className="text-xs text-gray-300">
                      Adding WHOOP, Polar, Xiaomi, Huawei, professional clinical equipment, and voice assistants
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🏥</span>
                      <h5 className="font-semibold text-white">Hospital Integration</h5>
                    </div>
                    <p className="text-xs text-gray-300">
                      Stryker smart beds, Philips ventilators, professional EKGs, and clinical information systems
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🎯</span>
                      <h5 className="font-semibold text-white">Global Market Access</h5>
                    </div>
                    <p className="text-xs text-gray-300">
                      Targeting Asia (Xiaomi), Europe (Nokia), and specialized markets with region-specific devices
                    </p>
                  </div>
                </div>

                {/* Current Integration Status */}
                <div className="p-4 mt-6 rounded-lg bg-gradient-to-r from-green-800/30 to-blue-800/30">
                  <h5 className="mb-2 font-semibold text-white">🔥 Phase 1 Active Integrations</h5>
                  <div className="grid gap-2 text-xs md:grid-cols-2">
                    <div className="text-green-300">
                      <p>✅ <span className="font-semibold">Apple Health:</span> Steps, Heart Rate, Sleep, ECG</p>
                      <p>✅ <span className="font-semibold">Withings:</span> Smart Scales, BP Monitors (API Ready)</p>
                      <p>✅ <span className="font-semibold">Garmin:</span> Fitness Tracking, GPS (API Ready)</p>
                    </div>
                    <div className="text-blue-300">
                      <p>✅ <span className="font-semibold">Samsung:</span> Galaxy Watch Ecosystem (Public API)</p>
                      <p>✅ <span className="font-semibold">Fitbit:</span> Comprehensive Health (Public API)</p>
                      <p>✅ <span className="font-semibold">Oura:</span> Sleep & Recovery (Public API)</p>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="p-3 mt-4 text-center rounded-lg bg-gradient-to-r from-purple-600 to-pink-600">
                  <p className="text-sm font-semibold text-white">
                    🚀 Ready to experience the future of healthcare? Connect your devices today and be part of the revolution!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Hero Health Score Section */}
          <div className="mb-8">
            <ModernHealthScoreHero 
              healthData={healthData}
              healthScore={healthScore}
              processedHealthData={processedHealthData}
              profileData={null}
              deviceStatus={deviceStatus}
              lastSync={lastSync}
              dataAvailability={dataAvailability}
              hasAnyData={hasAnyData}
              dataLoading={healthDataLoading}
              dataError={healthDataError}
              onRefresh={refreshHealthData}
            />
          </div>

          {/* Enhanced Tab Navigation */}
          <div className="mb-6">
            <div className="flex p-1 space-x-1 bg-white border border-gray-200 shadow-sm rounded-xl">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'nutrition', label: 'Nutrition', icon: Utensils }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all flex-1 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#02276F] to-blue-700 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <ModernOverviewTab 
              healthData={healthData}
              onNavigate={onNavigate}
              profileData={null}
              user={user}
              dataAvailability={dataAvailability}
              userActiveChallenges={userActiveChallenges}
              challengeProgress={challengeProgress}
              challengesLoading={challengesLoading}
              getActiveChallengeDetails={getActiveChallengeDetails}
              getActiveChallengeNames={getActiveChallengeNames}
              healthTrendsTimeRange={healthTrendsTimeRange}
              setHealthTrendsTimeRange={setHealthTrendsTimeRange}
            />
          )}
          {activeTab === 'nutrition' && (
            <ModernNutritionTab 
              healthData={healthData}
              dataAvailability={dataAvailability}
              onNavigate={onNavigate}
            />
          )}
        </div>
      </div>
    </div>
  );
}); // Closing memo wrapper

LandingPage.displayName = 'LandingPage';

// Modern Health Score Hero Component
const ModernHealthScoreHero = ({ 
  healthData, 
  healthScore,
  processedHealthData,
  profileData, 
  deviceStatus, 
  lastSync, 
  dataAvailability,
  hasAnyData,
  dataLoading, 
  dataError, 
  onRefresh 
}) => {
  const getScoreColor = (score) => {
    if (score >= 90) return '#00FC14';
    if (score >= 80) return '#10B981';
    if (score >= 70) return '#F1C40F';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const scoreColor = getScoreColor(healthData?.score?.overall || 0);

  // Data source indicator
  const getDataSourceInfo = () => {
    console.log('🔧 DATA SOURCE DEBUG:');
    console.log('- dataLoading:', dataLoading);
    console.log('- dataError:', dataError);
    console.log('- hasAnyData:', hasAnyData);
    console.log('- healthData?.devices:', healthData?.devices);
    console.log('- deviceStatus:', deviceStatus);
    
    if (dataLoading) {
      return { text: 'Syncing data...', color: 'text-blue-600', icon: <RefreshCw className="w-4 h-4 animate-spin" /> };
    }
    if (dataError) {
      return { text: 'Connection error', color: 'text-orange-600', icon: <AlertCircle className="w-4 h-4" /> };
    }
    
    // Check devices directly from healthData
    const deviceCount = healthData?.devices?.length || 0;
    const hasDevices = deviceCount > 0;
    
    console.log('- deviceCount:', deviceCount);
    console.log('- hasDevices:', hasDevices);
    
    // Show "Live from X devices" when we have devices, regardless of hasAnyData
    if (!dataLoading && !dataError && hasDevices) {
      console.log('✅ SHOWING: Live from devices');
      return { 
        text: `Live from ${deviceCount} device${deviceCount !== 1 ? 's' : ''}`, 
        color: 'text-green-600', 
        icon: <Wifi className="w-4 h-4" /> 
      };
    }
    
    // Fallback: check raw API response for devices
    const hasRawDeviceData = healthData?._rawApiResponse?.data?.length > 0;
    if (!dataLoading && !dataError && hasRawDeviceData) {
      const uniqueDevices = [...new Set(healthData._rawApiResponse.data.map(record => record._metadata?.deviceType))].filter(Boolean);
      const rawDeviceCount = uniqueDevices.length;
      if (rawDeviceCount > 0) {
        console.log('✅ SHOWING: Live from raw devices, count:', rawDeviceCount);
        return { 
          text: `Live from ${rawDeviceCount} device${rawDeviceCount !== 1 ? 's' : ''}`, 
          color: 'text-green-600', 
          icon: <Wifi className="w-4 h-4" /> 
        };
      }
    }
    
    console.log('❌ SHOWING: No devices connected');
    return { text: 'No devices connected', color: 'text-gray-600', icon: <WifiOff className="w-4 h-4" /> };
  };

  const dataSource = getDataSourceInfo();

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#02276F] via-blue-700 to-blue-800 rounded-3xl p-8 text-white shadow-xl">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute bg-blue-400 rounded-full -top-24 -right-24 w-96 h-96 blur-3xl animate-pulse"></div>
        <div className="absolute bg-purple-400 rounded-full -bottom-24 -left-24 w-96 h-96 blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="mb-2 text-3xl font-bold">Health Dashboard</h1>
            <p className="flex items-center gap-2 text-blue-100">
              Real-time health monitoring & AI-powered insights
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-white/10 ${dataSource.color}`}>
                {dataSource.icon}
                {dataSource.text}
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-blue-100">Active Plan{healthData?.activePlans?.length > 1 ? 's' : ''}</p>
              <p className="font-semibold">
                {healthData?.activePlans?.length > 0 
                  ? `${healthData.activePlans.length} enrolled`
                  : 'No active plan'
                }
              </p>
            </div>
            <button
              onClick={onRefresh}
              disabled={dataLoading}
              className="p-3 transition-all rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh health data"
            >
              <RefreshCw className={`w-5 h-5 ${dataLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Score with enhanced visualization */}
          <div className="lg:col-span-1">
            <div className="p-6 border bg-white/10 backdrop-blur-lg rounded-2xl border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Overall Health Score</h3>
                  {lastSync && (
                    <p className="mt-1 text-xs text-blue-200">
                      Last sync: {new Date(lastSync).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <Shield className="w-5 h-5 text-blue-300" />
              </div>
              
              <div className="relative flex items-center justify-center h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    data={[{ value: healthScore?.overallScore || 0, fill: getScoreColor(healthScore?.overallScore || 0) }]}
                    startAngle={180}
                    endAngle={-180}
                  >
                    <RadialBar
                      dataKey="value"
                      cornerRadius={10}
                      fill={getScoreColor(healthScore?.overallScore || 0)}
                      background={{ fill: 'rgba(255,255,255,0.1)' }}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold">{Math.round(healthScore?.overallScore || 0)}%</span>
                  <span className="text-sm opacity-80">{healthScore?.level || 'Calculating...'}</span>
                </div>
              </div>

              {/* Score Components */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                {healthScore?.breakdown?.slice(0, 6).map((category) => (
                  <div key={category.category} className="text-center">
                    <div className="text-xs capitalize opacity-70">{category.category}</div>
                    <div className="text-sm font-semibold">{category.score}%</div>
                  </div>
                )) || Object.entries(healthData?.score?.components || {}).map(([key, value]) => (
                  <div key={key} className="text-center">
                    <div className="text-xs capitalize opacity-70">{key}</div>
                    <div className="text-sm font-semibold">{value}%</div>
                  </div>
                ))}
              </div>

              {/* Data Quality Indicator */}
              <div className="p-3 mt-4 rounded-lg bg-white/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs">Data Completeness</span>
                  <span className="text-xs font-semibold">
                    {Math.round(processedHealthData?.dataCompleteness?.percentage || healthData?.dataQuality?.percentage || 85)}%
                  </span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-1.5">
                  <div 
                    className="bg-gradient-to-r from-blue-400 to-green-400 rounded-full h-1.5 transition-all duration-500"
                    style={{ width: `${processedHealthData?.dataCompleteness?.percentage || healthData?.dataQuality?.percentage || 85}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Live Metrics Grid - Enhanced */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { 
                  icon: Heart, 
                  label: 'Heart Rate', 
                  value: typeof healthData?.realTimeMetrics?.heartRate === 'object' ? healthData?.realTimeMetrics?.heartRate?.value : healthData?.realTimeMetrics?.heartRate, 
                  unit: 'bpm',
                  color: 'from-red-400 to-red-600',
                  available: dataAvailability?.heartRate
                },
                { 
                  icon: Activity, 
                  label: 'Steps', 
                  value: typeof healthData?.realTimeMetrics?.steps === 'object' ? healthData?.realTimeMetrics?.steps?.value || 0 : healthData?.realTimeMetrics?.steps || 0, 
                  unit: '',
                  color: 'from-blue-400 to-blue-600',
                  available: dataAvailability?.steps
                },
                { 
                  icon: Flame, 
                  label: 'Calories', 
                  value: typeof healthData?.realTimeMetrics?.calories === 'object' ? healthData?.realTimeMetrics?.calories?.value || 0 : healthData?.realTimeMetrics?.calories || 0, 
                  unit: 'kcal',
                  color: 'from-orange-400 to-orange-600',
                  available: dataAvailability?.calories
                },
                { 
                  icon: Droplets, 
                  label: 'Water', 
                  value: typeof healthData?.realTimeMetrics?.water === 'object' ? healthData?.realTimeMetrics?.water?.value || healthData?.wellness?.hydration?.glasses || 0 : healthData?.realTimeMetrics?.water || healthData?.wellness?.hydration?.glasses || 0,
                  unit: '/8',
                  color: 'from-cyan-400 to-cyan-600',
                  available: dataAvailability?.water || true // Always show water
                },
                { 
                  icon: Moon, 
                  label: 'Sleep', 
                  value: (() => {
                    const sleepValue = typeof healthData?.realTimeMetrics?.sleep === 'object' ? healthData?.realTimeMetrics?.sleep?.value : healthData?.realTimeMetrics?.sleep;
                    // Oura Ring provides sleep in minutes if > 24, convert to hours
                    return sleepValue && sleepValue > 24 ? Number((sleepValue / 60).toFixed(1)) : sleepValue;
                  })(), 
                  unit: 'h',
                  color: 'from-purple-400 to-purple-600',
                  available: dataAvailability?.sleep
                },
                { 
                  icon: Brain, 
                  label: 'Stress', 
                  value: typeof healthData?.realTimeMetrics?.stress === 'object' ? healthData?.realTimeMetrics?.stress?.value || 'Unknown' : healthData?.realTimeMetrics?.stress || 'Unknown', 
                  unit: '',
                  color: 'from-green-400 to-green-600',
                  available: dataAvailability?.stress
                },
                { 
                  icon: Thermometer, 
                  label: 'Temperature', 
                  value: (() => {
                    let temp = healthData?.vitals?.temperature?.value || 
                               healthData?.vitals?.bodyTemperature?.value || 
                               healthData?.realTimeMetrics?.temperature ||
                               healthData?.vitalSigns?.bodyTemperature?.value ||
                               healthData?.vitalSigns?.bodyTemperature ||
                               null;
                    
                    // DEBUG: Log the temperature data
                    console.log('🌡️ TEMPERATURE DEBUG:', {
                      rawTemp: temp,
                      type: typeof temp,
                      healthData: {
                        vitalsTemp: healthData?.vitals?.temperature?.value,
                        vitalsBodyTemp: healthData?.vitals?.bodyTemperature?.value,
                        realTimeTemp: healthData?.realTimeMetrics?.temperature,
                        vitalSignsTemp: healthData?.vitalSigns?.bodyTemperature?.value,
                        vitalSignsBodyTemp: healthData?.vitalSigns?.bodyTemperature
                      }
                    });
                    
                    // Treat 0 as invalid temperature data
                    if (temp !== null && temp !== 0 && typeof temp === 'number' && !isNaN(temp)) {
                      // If temperature is in Celsius range (likely 35-42°C), convert to Fahrenheit
                      if (temp >= 35 && temp <= 42) {
                        temp = (temp * 9/5) + 32;
                      }
                      // If already in Fahrenheit range (95-108°F), keep as is
                      const result = Number(temp.toFixed(1));
                      console.log('🌡️ TEMPERATURE RESULT:', result);
                      return result;
                    } else {
                      console.log('🌡️ TEMPERATURE INVALID:', temp, 'returning null');
                      return null; // Return null for invalid temperature data including 0
                    }
                  })(), 
                  unit: '°F',
                  color: 'from-yellow-400 to-yellow-600',
                  available: dataAvailability?.temperature
                },
                { 
                  icon: Pill, 
                  label: 'Medications', 
                  value: (() => {
                    const medicationsData = healthData?.medications;
                    if (!medicationsData) return 0;
                    
                    let totalCount = 0;
                    
                    // Count prescription medications
                    if (Array.isArray(medicationsData.currentMedications)) {
                      totalCount += medicationsData.currentMedications.length;
                    }
                    
                    // Count supplements
                    if (medicationsData.supplements?.vitaminsSupplements) {
                      totalCount += medicationsData.supplements.vitaminsSupplements.filter(item => item && item.trim() !== '' && item.trim() !== ',').length;
                    }
                    
                    // Count herbal remedies
                    if (medicationsData.supplements?.herbalRemedies) {
                      totalCount += medicationsData.supplements.herbalRemedies.filter(item => item && item.trim() !== '' && item.trim() !== ',').length;
                    }
                    
                    return totalCount;
                  })(), 
                  unit: 'active',
                  color: 'from-pink-400 to-pink-600',
                  available: true
                },

              ].map((metric, idx) => (
                <MetricTile key={idx} metric={metric} />
              ))}
            </div>

            {/* Connected Devices Strip */}
            {(healthData?.devices?.length > 0 || healthData?._rawApiResponse?.data?.length > 0) && (
              <div className="p-3 mt-4 bg-white/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-4 h-4 opacity-60" />
                    <span className="text-sm">Connected Devices:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Use devices array if available, otherwise extract from raw data */}
                    {healthData?.devices?.length > 0 ? (
                      healthData.devices.slice(0, 3).map((device, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs rounded-full bg-white/20">
                          {device.name || device.type || 'Unknown Device'}
                        </span>
                      ))
                    ) : (
                      // Extract device types from raw API response as fallback
                      [...new Set(healthData._rawApiResponse.data.map(record => {
                        const deviceType = record._metadata?.deviceType;
                        if (deviceType === 'oura-ring') return 'Oura Ring';
                        if (deviceType === 'iphone-healthkit') return 'iPhone HealthKit';
                        if (deviceType === 'withings-body-scan') return 'Withings Body Scan';
                        if (deviceType === 'withings-bpm-connect') return 'Withings BPM';
                        if (deviceType === 'withings-uscan-nutrio') return 'Withings Uscan-Nutrio';
                        return deviceType?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
                      }).filter(Boolean))].slice(0, 3).map((deviceName, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs rounded-full bg-white/20">
                          {deviceName}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Metric Tile Component
const MetricTile = ({ metric }) => {
  const { icon: Icon, label, value, unit, color, available } = metric;
  
  if (!available && value === null) {
    return (
      <div className="p-4 border bg-white/5 backdrop-blur-lg rounded-xl border-white/10">
        <div className="flex items-center justify-between mb-2">
          <Icon className="w-5 h-5 opacity-40" />
          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
        </div>
        <p className="text-xs opacity-50">{label}</p>
        <p className="text-lg font-semibold opacity-30">--</p>
      </div>
    );
  }

  return (
    <div className="p-4 transition-all border cursor-pointer bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 border-white/10">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 opacity-80" />
        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${color} animate-pulse`}></div>
      </div>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-lg font-semibold">
      {typeof value === 'number' ? value.toLocaleString() : value}
      {unit && <span className="text-sm font-normal opacity-70"> {unit}</span>}
    </p>
    </div>
  );
};

// Modern Overview Tab
const ModernOverviewTab = ({ 
  healthData, 
  onNavigate, 
  profileData, 
  user, 
  dataAvailability,
  userActiveChallenges = [],
  challengeProgress = {},
  challengesLoading = false,
  getActiveChallengeDetails = () => [],
  getActiveChallengeNames = () => [],
  healthTrendsTimeRange,
  setHealthTrendsTimeRange
}) => {
  const activePlan = profileData?.enrolledPlans?.[0];
  
  return (
    <div className="space-y-6">
      {/* Health Alerts if any */}
      {healthData?.alerts?.length > 0 && (
        <div className="p-4 border border-red-200 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="mb-2 font-semibold text-red-900">Health Alerts</h4>
              {healthData.alerts.map((alert, idx) => (
                <p key={idx} className="mb-1 text-sm text-red-700">• {alert.message}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Insights - Enhanced */}
      <div className="p-6 border border-purple-200 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              AI Insights - {activePlan?.name || 'General Health'}
            </h3>
            <p className="text-sm text-gray-600">Personalized recommendations powered by Aman AI</p>
          </div>
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(healthData?.recommendations || getDefaultInsights()).slice(0, 3).map((insight, idx) => (
            <div key={idx} className="p-4 transition-all bg-white shadow-sm rounded-xl hover:shadow-md">
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${
                  insight.priority === 'high' ? 'bg-red-100' : 
                  insight.priority === 'medium' ? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  <Info className={`w-5 h-5 ${
                    insight.priority === 'high' ? 'text-red-600' : 
                    insight.priority === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <h4 className="mb-1 font-medium text-gray-900">{insight.message || insight.title}</h4>
                  <p className="text-sm text-gray-600">{insight.target || insight.description}</p>
                  {insight.action && (
                    <button className="mt-2 text-sm font-medium text-purple-600 hover:text-purple-800">
                      {insight.action} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button 
          onClick={() => onNavigate('ai-coach')}
          className="w-full py-3 mt-4 font-medium text-white transition-all rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 hover:shadow-lg"
        >
          Chat with Aman AI for More Insights
        </button>
      </div>

      {/* Activity & Goals Grid - Enhanced */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Today's Activity */}
        <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Today's Activity</h3>
          
          <div className="space-y-4">
            <EnhancedActivityRing 
              steps={typeof healthData?.realTimeMetrics?.steps === 'object' ? healthData?.realTimeMetrics?.steps?.value || 0 : healthData?.realTimeMetrics?.steps || 0} 
              calories={typeof healthData?.realTimeMetrics?.calories === 'object' ? healthData?.realTimeMetrics?.calories?.value || 0 : healthData?.realTimeMetrics?.calories || 0}
              activeMinutes={typeof healthData?.activity?.activeMinutes === 'object' ? healthData?.activity?.activeMinutes?.value || 0 : healthData?.activity?.activeMinutes || 0}
              available={dataAvailability?.steps}
            />
            
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Daily Goal Progress</span>
                <span className="font-semibold text-[#02276F]">
                  {Math.round(((typeof healthData?.realTimeMetrics?.steps === 'object' ? healthData?.realTimeMetrics?.steps?.value || 0 : healthData?.realTimeMetrics?.steps || 0) / 10000) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Challenges */}
        <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Challenges</h3>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                {getActiveChallengeDetails().length} Active
              </span>
              <Trophy className="w-5 h-5 text-yellow-500" />
            </div>
          </div>
          
          <ActiveChallenges 
            userId={user?.localAccountId} 
            onNavigate={onNavigate}
            userActiveChallenges={userActiveChallenges}
            challengeProgress={challengeProgress}
            challengesLoading={challengesLoading}
            getActiveChallengeDetails={getActiveChallengeDetails}
            getActiveChallengeNames={getActiveChallengeNames}
          />
        </div>
      </div>

      {/* Health Trends - Enhanced */}
      <div className="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Health Trends</h3>
        </div>
        
        <EnhancedHealthTrendsChart 
          healthData={healthData} 
          dataAvailability={dataAvailability}
          timeRange={healthTrendsTimeRange}
          setTimeRange={setHealthTrendsTimeRange}
        />
      </div>
    </div>
  );
};

// Modern Nutrition Tab - Enhanced
const ModernNutritionTab = ({ healthData, dataAvailability }) => {
  return (
    <div className="space-y-6">
      {/* Phase Status Banner */}
      <div className="p-4 border border-blue-200 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="mb-1 text-lg font-semibold text-gray-900">Nutrition Features Roadmap</h3>
            <p className="text-sm text-gray-600">Clear visibility into current capabilities and upcoming features</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                  ✅ Live Now
                </span>
                <p className="mt-1 text-xs text-gray-600">Basic Tracking</p>
              </div>
              <div className="text-center">
                <span className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                  🚀 Phase 2
                </span>
                <p className="mt-1 text-xs text-gray-600">AI-Powered</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Enhanced Nutrition Overview */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Daily Nutrition Score */}
        <div className="relative p-6 border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
          <div className="absolute z-10 top-4 right-4">
            <span className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
              Phase 2
            </span>
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Nutrition Score</h3>
            <div className="text-3xl font-bold text-green-600 opacity-60">85%</div>
          </div>
          
          <div className="space-y-3 opacity-70">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Quality</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-green-500 rounded-full w-17"></div>
                </div>
                <span className="text-sm font-medium">85%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Balance</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-200 rounded-full">
                  <div className="w-16 h-2 bg-blue-500 rounded-full"></div>
                </div>
                <span className="text-sm font-medium">80%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Timing</span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-purple-500 rounded-full w-18"></div>
                </div>
                <span className="text-sm font-medium">90%</span>
              </div>
            </div>
          </div>
          
          <div className="p-3 mt-4 rounded-lg bg-white/60">
            <p className="text-xs text-center text-gray-600">
              AI-powered nutrition analysis coming in Phase 2
            </p>
          </div>
        </div>

        {/* Calorie Tracking */}
        <div className="p-6 border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Calories Today</h3>
            <Flame className="w-6 h-6 text-blue-600" />
          </div>
          
          <div className="mb-4 text-center">
            <p className="text-3xl font-bold text-blue-600">1,650</p>
            <p className="text-sm text-gray-600">of 2,000 target</p>
          </div>
          
          <div className="w-full h-3 mb-2 bg-gray-200 rounded-full">
            <div className="w-4/5 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
          </div>
          
          <div className="flex justify-between text-sm text-gray-600">
            <span>350 remaining</span>
            <span>83% goal</span>
          </div>
        </div>

        {/* Hydration Tracking */}
        <div className="p-6 border border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Hydration</h3>
            <Droplets className="w-6 h-6 text-cyan-600" />
          </div>
          
          <div className="mb-4 text-center">
            <p className="text-3xl font-bold text-cyan-600">10</p>
            <p className="text-sm text-gray-600">of 8 glasses</p>
          </div>
          
          <div className="flex justify-center gap-1 mb-3">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div 
                key={idx} 
                className={`w-4 h-6 rounded-sm ${
                  idx < 10 
                    ? 'bg-cyan-500' 
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          
          <button className="w-full py-2 text-sm transition-colors rounded-lg bg-cyan-100 text-cyan-700 hover:bg-cyan-200">
            + Add Glass
          </button>
        </div>
      </div>

      {/* Macro Nutrients Breakdown */}
      <div className="relative p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        {/* Phase 2 Badge */}
        <div className="absolute z-10 top-4 right-4">
          <span className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 border border-blue-200 rounded-full">
            Coming in Phase 2
          </span>
        </div>
        
        <div className="relative">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">Macronutrients Breakdown</h3>
          
          {/* Overlay for Phase 2 */}
          <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] rounded-xl z-10 flex items-center justify-center">
            <div className="p-6 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
                <span className="text-2xl">🚀</span>
              </div>
              <h4 className="mb-2 text-lg font-semibold text-gray-900">Advanced Nutrition Tracking</h4>
              <p className="mb-3 text-sm text-gray-600">Detailed macro analysis with AI-powered recommendations</p>
              <span className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg">
                Phase 2 Release
              </span>
            </div>
          </div>
        
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Protein */}
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto mb-3">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                  <circle 
                    cx="48" cy="48" r="40" 
                    stroke="#10B981" 
                    strokeWidth="8" 
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - 0.85)}`}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-green-600">85g</span>
                  <span className="text-xs text-gray-600">85%</span>
                </div>
              </div>
              <h4 className="font-semibold text-gray-900">Protein</h4>
              <p className="text-sm text-gray-600">Target: 100g</p>
            </div>

            {/* Carbohydrates */}
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto mb-3">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                  <circle 
                    cx="48" cy="48" r="40" 
                    stroke="#F59E0B" 
                    strokeWidth="8" 
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - 0.72)}`}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-orange-600">180g</span>
                  <span className="text-xs text-gray-600">72%</span>
                </div>
              </div>
              <h4 className="font-semibold text-gray-900">Carbs</h4>
              <p className="text-sm text-gray-600">Target: 250g</p>
            </div>

            {/* Fats */}
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto mb-3">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                  <circle 
                    cx="48" cy="48" r="40" 
                    stroke="#EF4444" 
                    strokeWidth="8" 
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - 0.85)}`}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-red-600">55g</span>
                  <span className="text-xs text-gray-600">85%</span>
                </div>
              </div>
              <h4 className="font-semibold text-gray-900">Fats</h4>
              <p className="text-sm text-gray-600">Target: 65g</p>
            </div>
          </div>
        </div>
      </div>

      {/* Meal Tracking */}
      <div className="relative p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Today's Meals</h3>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 text-xs font-semibold text-orange-700 bg-orange-100 border border-orange-200 rounded-full">
              Coming in Phase 2
            </span>
            <button
              onClick={() => alert('Food scanner feature coming in Phase 2!')}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#02276F] to-blue-700 text-white rounded-lg hover:shadow-md transition-all opacity-75"
              disabled
            >
              <Camera className="w-4 h-4" />
              <span>Scan Food</span>
            </button>
          </div>
        </div>

        {/* Overlay for Phase 2 */}
        <div className="absolute inset-6 bg-white/60 backdrop-blur-[1px] rounded-xl z-10 flex items-center justify-center mt-16">
          <div className="p-6 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full">
              <span className="text-2xl">📱</span>
            </div>
            <h4 className="mb-2 text-lg font-semibold text-gray-900">Smart Meal Logging</h4>
            <p className="mb-3 text-sm text-gray-600">AI-powered food recognition and nutritional analysis</p>
            <span className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg">
              Phase 2 Feature
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Breakfast */}
          <div className="p-4 border border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-yellow-600" />
                <h4 className="font-semibold text-gray-900">Breakfast</h4>
              </div>
              <span className="text-sm text-gray-600">420 cal</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Oatmeal with berries</p>
              <p>• Greek yogurt</p>
              <p>• Coffee</p>
            </div>
            <button className="w-full py-2 mt-3 text-sm text-yellow-700 transition-colors border border-yellow-300 rounded-lg hover:bg-yellow-100 opacity-60" disabled>
              + Add Food
            </button>
          </div>

          {/* Lunch */}
          <div className="p-4 border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-gray-900">Lunch</h4>
              </div>
              <span className="text-sm text-gray-600">580 cal</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Quinoa salad</p>
              <p>• Grilled chicken</p>
              <p>• Mixed vegetables</p>
            </div>
            <button className="w-full py-2 mt-3 text-sm text-green-700 transition-colors border border-green-300 rounded-lg hover:bg-green-100 opacity-60" disabled>
              + Add Food
            </button>
          </div>

          {/* Dinner */}
          <div className="p-4 border border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sunset className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Dinner</h4>
              </div>
              <span className="text-sm text-gray-600">650 cal</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Salmon fillet</p>
              <p>• Brown rice</p>
              <p>• Steamed broccoli</p>
            </div>
            <button className="w-full py-2 mt-3 text-sm text-purple-700 transition-colors border border-purple-300 rounded-lg hover:bg-purple-100 opacity-60" disabled>
              + Add Food
            </button>
          </div>

          {/* Snacks */}
          <div className="p-4 border border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Coffee className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">Snacks</h4>
              </div>
              <span className="text-sm text-gray-600">200 cal</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Mixed nuts</p>
              <p>• Apple slices</p>
              <p>• Green tea</p>
            </div>
            <button className="w-full py-2 mt-3 text-sm text-blue-700 transition-colors border border-blue-300 rounded-lg hover:bg-blue-100 opacity-60" disabled>
              + Add Food
            </button>
          </div>
        </div>
      </div>

      {/* Food Partners */}
      <div className="relative p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Healthy Food Partners</h3>
          <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 border border-green-200 rounded-full">
            Coming in Phase 2
          </span>
        </div>
        
        {/* Overlay for Phase 2 */}
        <div className="absolute inset-6 bg-white/60 backdrop-blur-[1px] rounded-xl z-10 flex items-center justify-center mt-16">
          <div className="p-6 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
              <span className="text-2xl">🤝</span>
            </div>
            <h4 className="mb-2 text-lg font-semibold text-gray-900">Partner Integration</h4>
            <p className="mb-3 text-sm text-gray-600">Seamless ordering from healthy food delivery partners</p>
            <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg">
              Phase 2 Partnership
            </span>
          </div>
        </div>
        
        <EnhancedFoodPartners />
      </div>
    </div>
  );
};

// Modern Vitals Tab
// Enhanced Helper Components

const EnhancedActivityRing = ({ steps, calories, activeMinutes, available }) => {
  if (!available) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No activity data available</p>
          <p className="mt-1 text-sm text-gray-400">Connect a fitness tracker to see your activity</p>
        </div>
      </div>
    );
  }

  const stepsProgress = (steps / 10000) * 100;
  const caloriesProgress = (calories / 500) * 100;
  const activeProgress = (activeMinutes / 30) * 100;
  
  return (
    <div className="flex items-center justify-center">
      <div className="relative w-48 h-48">
        <svg className="w-full h-full transform -rotate-90">
          {/* Steps Ring */}
          <circle cx="96" cy="96" r="88" stroke="#E5E7EB" strokeWidth="8" fill="none" />
          <circle 
            cx="96" cy="96" r="88" 
            stroke="#02276F" 
            strokeWidth="8" 
            fill="none"
            strokeDasharray={`${2 * Math.PI * 88}`}
            strokeDashoffset={`${2 * Math.PI * 88 * (1 - stepsProgress / 100)}`}
            className="transition-all duration-500"
          />
          
          {/* Calories Ring */}
          <circle cx="96" cy="96" r="72" stroke="#E5E7EB" strokeWidth="8" fill="none" />
          <circle 
            cx="96" cy="96" r="72" 
            stroke="#F59E0B" 
            strokeWidth="8" 
            fill="none"
            strokeDasharray={`${2 * Math.PI * 72}`}
            strokeDashoffset={`${2 * Math.PI * 72 * (1 - caloriesProgress / 100)}`}
            className="transition-all duration-500"
          />
          
          {/* Active Minutes Ring */}
          <circle cx="96" cy="96" r="56" stroke="#E5E7EB" strokeWidth="8" fill="none" />
          <circle 
            cx="96" cy="96" r="56" 
            stroke="#10B981" 
            strokeWidth="8" 
            fill="none"
            strokeDasharray={`${2 * Math.PI * 56}`}
            strokeDashoffset={`${2 * Math.PI * 56 * (1 - activeProgress / 100)}`}
            className="transition-all duration-500"
          />
        </svg>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Activity className="w-8 h-8 mb-1 text-gray-600" />
          <span className="text-2xl font-bold text-gray-900">{steps.toLocaleString()}</span>
          <span className="text-xs text-gray-600">steps today</span>
        </div>
      </div>
      
      <div className="ml-8 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-[#02276F] rounded-full"></div>
          <span className="text-sm text-gray-600">Steps: {steps.toLocaleString()} / 10,000</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-[#F59E0B] rounded-full"></div>
          <span className="text-sm text-gray-600">Calories: {calories} / 500 kcal</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-[#10B981] rounded-full"></div>
          <span className="text-sm text-gray-600">Active: {activeMinutes} / 30 min</span>
        </div>
      </div>
    </div>
  );
};

const EnhancedHealthTrendsChart = ({ healthData, dataAvailability, timeRange, setTimeRange }) => {
  const [selectedCategory, setSelectedCategory] = useState('activity');
  const [historicalData, setHistoricalData] = useState({});

  // Extract historical data from the main health data API response
  const extractHistoricalDataFromResponse = useCallback(() => {
    console.log('📊 EXTRACTING: Historical data from main API response...');
    
    // Check if we have the raw health data response
    if (healthData?._rawApiResponse?.data && Array.isArray(healthData._rawApiResponse.data)) {
      const historicalMap = {};
      
      console.log(`📊 PROCESSING: ${healthData._rawApiResponse.data.length} records from API response`);
      
      // Debug: Show all dates in the API response
      const allDates = healthData._rawApiResponse.data.map(record => record._metadata?.fileDate).filter(Boolean);
      const uniqueDates = [...new Set(allDates)].sort();
      console.log('📅 ALL DATES IN API RESPONSE:', uniqueDates);
      console.log('📅 DATE RANGE:', uniqueDates[0], 'to', uniqueDates[uniqueDates.length - 1]);
      
      // Process all data records from the API response
      healthData._rawApiResponse.data.forEach((record, index) => {
        // Extract date from file metadata
        const fileDate = record._metadata?.fileDate;
        if (fileDate) {
          const dateStr = new Date(fileDate).toISOString().split('T')[0];
          
          // Skip today's data as we handle that separately
          const today = new Date().toISOString().split('T')[0];
          if (dateStr === today) {
            console.log(`📊 SKIP TODAY: Skipping today's data for ${dateStr}`);
            return;
          }
          
          // Extract health metrics from the record with comprehensive device support
          const extractedData = {
            // Steps - check multiple device paths
            steps: record.healthMetrics?.activity?.steps?.value || 
                   record.healthMetrics?.activity?.steps || 
                   record.healthMetrics?.activity?.dailySteps?.value ||
                   record.healthMetrics?.activity?.dailySteps ||
                   null,
            
            // Calories - check multiple device paths (active calories, total calories, etc.)
            calories: record.healthMetrics?.activity?.calories?.value || 
                     record.healthMetrics?.activity?.calories || 
                     record.healthMetrics?.activity?.caloriesActive?.value ||
                     record.healthMetrics?.activity?.caloriesActive ||
                     record.healthMetrics?.activity?.caloriesTotal?.value ||
                     record.healthMetrics?.activity?.caloriesTotal ||
                     record.healthMetrics?.activity?.activeCalories?.value ||
                     record.healthMetrics?.activity?.activeCalories ||
                     record.healthMetrics?.activity?.caloriesBurned?.value ||
                     record.healthMetrics?.activity?.caloriesBurned ||
                     null,
            
            // Sleep - check multiple device paths
            sleep: record.healthMetrics?.sleep?.duration?.value || 
                   record.healthMetrics?.sleep?.duration || 
                   record.healthMetrics?.sleep?.totalSleep?.value ||
                   record.healthMetrics?.sleep?.totalSleep ||
                   record.healthMetrics?.lifestyle?.sleep?.value ||
                   record.healthMetrics?.lifestyle?.sleep ||
                   null,
            
            // Heart Rate - check multiple device paths
            heartRate: record.healthMetrics?.cardiovascular?.heartRate?.value || 
                      record.healthMetrics?.cardiovascular?.heartRate || 
                      record.healthMetrics?.vitalSigns?.heartRate?.value ||
                      record.healthMetrics?.vitalSigns?.heartRate ||
                      record.healthMetrics?.vitalSigns?.heartRate?.average ||
                      null,
            
            // Stress - check multiple device paths
            stress: record.healthMetrics?.wellness?.stress?.value || 
                   record.healthMetrics?.wellness?.stress || 
                   record.healthMetrics?.lifestyle?.stress?.value ||
                   record.healthMetrics?.lifestyle?.stress ||
                   null,
            
            // Water/Hydration - Apple HealthKit first, then other device paths + U-Scan conversion
            water: (() => {
              // Prioritize Apple HealthKit water intake paths first (lifestyle.waterIntake is PRIMARY for iPhone)
              let waterValue = record.healthMetrics?.lifestyle?.waterIntake?.value ||  // iPhone HealthKit PRIMARY
                              record.healthMetrics?.lifestyle?.waterIntake ||          // iPhone HealthKit direct
                              record.healthMetrics?.nutrition?.waterIntake?.value || 
                              record.healthMetrics?.nutrition?.waterIntake ||
                              record.healthMetrics?.hydration?.waterIntake?.value ||
                              record.healthMetrics?.hydration?.waterIntake ||
                              record.healthMetrics?.wellness?.hydration?.glasses || 
                              record.healthMetrics?.wellness?.hydration?.value ||
                              record.healthMetrics?.lifestyle?.hydration?.glasses ||
                              record.healthMetrics?.lifestyle?.hydration?.value ||
                              record.healthMetrics?.nutrition?.hydration?.glasses ||
                              record.healthMetrics?.nutrition?.hydration?.value ||
                              record.healthMetrics?.hydration?.glasses ||
                              record.healthMetrics?.hydration?.value ||
                              null;
              
              // Convert Apple HealthKit water intake from ML to glasses (priority conversion)
              if (waterValue && typeof waterValue === 'number' && waterValue > 50) {
                // If value > 50, assume it's in ML from Apple HealthKit and convert to glasses
                waterValue = Math.round(waterValue / 240); // 1 glass = 240ml
                console.log(`💧 APPLE CONVERTED: ${waterValue * 240}ml → ${waterValue} glasses for ${record._metadata?.fileDate || 'unknown date'}`);
              }
              
              // Convert Withings U-Scan hydrationLevel to glasses (qualitative to quantitative)
              if (!waterValue && record.healthMetrics?.urinalysis?.hydrationLevel) {
                const hydrationLevel = record.healthMetrics.urinalysis.hydrationLevel;
                switch (hydrationLevel) {
                  case 'well_hydrated':
                    waterValue = 8; // Excellent hydration = 8 glasses
                    break;
                  case 'adequately_hydrated':
                    waterValue = 6; // Good hydration = 6 glasses  
                    break;
                  case 'dehydrated':
                    waterValue = 3; // Poor hydration = 3 glasses
                    break;
                  default:
                    waterValue = 4; // Default moderate hydration
                }
                console.log(`💧 USCAN CONVERTED: ${hydrationLevel} → ${waterValue} glasses for ${record._metadata?.fileDate || 'unknown date'}`);
              }
              
              // Check if we have ml values that need converting to glasses
              if (!waterValue && record.healthMetrics?.hydration?.ml) {
                waterValue = Math.round(record.healthMetrics.hydration.ml / 240); // 240ml per glass
              }
              
              return waterValue;
            })(),
            
            // Temperature - comprehensive paths with unit normalization
            temperature: (() => {
              let temp = record.healthMetrics?.vitals?.temperature?.value || 
                        record.healthMetrics?.vitalSigns?.bodyTemperature?.value ||
                        record.healthMetrics?.vitalSigns?.bodyTemperature ||
                        record.healthMetrics?.vitals?.bodyTemperature?.value ||
                        record.healthMetrics?.vitals?.bodyTemperature ||
                        null;
              
              // Normalize temperature to Fahrenheit for consistency
              if (temp !== null && temp !== 0 && typeof temp === 'number' && !isNaN(temp)) {
                // If temperature is in Celsius range (likely 35-42°C), convert to Fahrenheit
                if (temp >= 35 && temp <= 42) {
                  temp = (temp * 9/5) + 32;
                }
                // If already in Fahrenheit range (95-108°F), keep as is
                temp = Number(temp.toFixed(1));
              } else {
                temp = null; // Reset to null if not a valid number or is 0
              }
              
              return temp;
            })(),
            
            // Blood Oxygen - check multiple device paths
            bloodOxygen: record.healthMetrics?.cardiovascular?.bloodOxygen?.value || 
                        record.healthMetrics?.vitalSigns?.oxygenSaturation?.value ||
                        record.healthMetrics?.vitalSigns?.oxygenSaturation ||
                        null,
            
            // Blood Pressure - check multiple device paths
            bloodPressure: record.healthMetrics?.cardiovascular?.bloodPressure?.value || 
                          record.healthMetrics?.vitalSigns?.bloodPressure?.systolic ||
                          record.healthMetrics?.vitalSigns?.bloodPressure?.value ||
                          null,
            
            // Weight - check multiple device paths
            weight: record.healthMetrics?.bodyComposition?.weight?.value || 
                   record.healthMetrics?.bodyComposition?.weight ||
                   null
          };
          
          // Convert sleep from minutes to hours if needed
          if (extractedData.sleep && extractedData.sleep > 24) {
            extractedData.sleep = Number((extractedData.sleep / 60).toFixed(1));
          }
          
          // Aggregate data for the same date (multiple readings per day)
          if (!historicalMap[dateStr]) {
            historicalMap[dateStr] = extractedData;
            console.log(`📊 NEW DATE: Created entry for ${dateStr}`);
          } else {
            // Take the highest/latest values for the day
            Object.keys(extractedData).forEach(key => {
              if (extractedData[key] !== null) {
                if (historicalMap[dateStr][key] === null || 
                    (typeof extractedData[key] === 'number' && 
                     extractedData[key] > (historicalMap[dateStr][key] || 0))) {
                  historicalMap[dateStr][key] = extractedData[key];
                }
              }
            });
            console.log(`📊 UPDATED: Aggregated data for ${dateStr}`);
          }
        }
      });
      
      // Update historical data state
      const extractedDates = Object.keys(historicalMap);
      if (extractedDates.length > 0) {
        console.log(`📊 SUCCESS: Extracted historical data for ${extractedDates.length} dates:`, extractedDates);
        console.log('📊 SAMPLE DATA:', Object.keys(historicalMap).slice(0, 3).map(date => ({
          date,
          steps: historicalMap[date].steps,
          calories: historicalMap[date].calories
        })));
        
        setHistoricalData(prev => ({
          ...prev,
          ...historicalMap
        }));
      } else {
        console.log('📊 NO HISTORICAL: No historical dates found in API response');
      }
    } else {
      console.log('📊 NO RAW DATA: No raw API response data available for historical extraction');
      console.log('📊 DEBUG: healthData keys:', healthData ? Object.keys(healthData) : 'null');
      console.log('📊 DEBUG: _rawApiResponse exists:', !!healthData?._rawApiResponse);
    }
  }, [healthData]);

  // Extract historical data when health data loads
  useEffect(() => {
    if (healthData) {
      extractHistoricalDataFromResponse();
    }
  }, [healthData, extractHistoricalDataFromResponse]);

  // Simplified fetchHistoricalData - no longer needed for separate API calls
  const fetchHistoricalData = useCallback(async (date) => {
    const dateStr = date.toISOString().split('T')[0];
    console.log(`📊 FETCH HISTORICAL: Checking cached data for ${dateStr}`);
    
    // Check if we already have this data cached
    if (historicalData[dateStr]) {
      console.log(`📊 CACHED: Using existing data for ${dateStr}:`, historicalData[dateStr]);
      return historicalData[dateStr];
    }
    
    console.log(`📊 NOT CACHED: No data available for ${dateStr}`);
    return null;
  }, [historicalData]);

  // Automatically fetch historical data when component loads or timeRange changes
  useEffect(() => {
    const loadHistoricalData = async () => {
      console.log('📊 AUTO FETCH: Loading historical data for chart...', timeRange);
      
      const today = new Date();
      const daysMap = {
        '7D': 7,
        '30D': 30,
        '60D': 60,
        '90D': 90,
        '30days': 30,
        '60days': 60,
        '90days': 90
      };
      
      const numDays = daysMap[timeRange] || 7;
      console.log(`📊 AUTO FETCH: Loading ${numDays} days of historical data`);
      
      const fetchPromises = [];
      
      // Fetch data for all days in the range (except today)
      for (let i = numDays - 1; i >= 1; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        // Only fetch if we don't already have this data cached
        if (!historicalData[dateStr]) {
          console.log(`📊 AUTO FETCH: Queuing fetch for ${dateStr}`);
          fetchPromises.push(fetchHistoricalData(date));
        } else {
          console.log(`📊 AUTO FETCH: Already have data for ${dateStr}`);
        }
      }
      
      // Execute all fetches in parallel
      if (fetchPromises.length > 0) {
        try {
          console.log(`📊 AUTO FETCH: Starting ${fetchPromises.length} parallel requests...`);
          await Promise.all(fetchPromises);
          console.log('📊 AUTO FETCH: Historical data loading complete!');
        } catch (error) {
          console.error('📊 AUTO FETCH: Error loading historical data:', error);
        }
      } else {
        console.log('📊 AUTO FETCH: All historical data already cached');
      }
    };
    
    // Only fetch if we don't have data already and timeRange is set
    if (timeRange) {
      loadHistoricalData();
    }
  }, [timeRange, fetchHistoricalData, historicalData]);

  // Debug historical data updates
  useEffect(() => {
    const keys = Object.keys(historicalData);
    console.log(`📊 HISTORICAL DATA UPDATE: Now have ${keys.length} cached days:`, keys);
    if (keys.length > 0) {
      console.log('📊 HISTORICAL DATA SAMPLE:', historicalData[keys[0]]);
    }
  }, [historicalData]);

  // Generate REAL health data trends based on selected time range - NO MORE MOCK DATA!
  const generateTrendData = useCallback(() => {
    console.log('📊 REAL DATA ONLY: Generating chart data from live health data');
    console.log('📊 FUNCTION CALLED: generateTrendData with timeRange:', timeRange);
    
    // Safety check: return empty array if healthData is not available
    if (!healthData) {
      console.log('📊 SAFETY: healthData not available yet, returning empty array');
      return [];
    }
    
    const today = new Date();
    const data = [];
    
    // Determine number of days based on timeRange
    const daysMap = {
      '7D': 7,
      '30D': 30,
      '60D': 60,
      '90D': 90,
      // Legacy support for old format
      '7days': 7,
      '30days': 30,
      '60days': 60,
      '90days': 90
    };
    
    const numDays = daysMap[timeRange] || 7;
    
    console.log(`📊 CHART GENERATION: timeRange=${timeRange}, numDays=${numDays}`);
    console.log(`📊 CHART GENERATION: Available historical dates:`, Object.keys(historicalData).sort());
    
    // Generate data points from oldest to newest (LEFT TO RIGHT)
    for (let i = numDays - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      // Format day name based on time range
      let dayName;
      if (numDays <= 7) {
        dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      } else {
        dayName = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      const isToday = i === 0;
      
      if (isToday && healthData?.realTimeMetrics) {
        // TODAY: Use real current data with comprehensive device support
        const steps = (healthData.realTimeMetrics.steps && typeof healthData.realTimeMetrics.steps === 'object') 
          ? (healthData.realTimeMetrics.steps.value || 0)
          : (healthData.realTimeMetrics.steps || 0);
        
        // Enhanced calories extraction - check multiple paths for different devices
        const calories = (healthData.realTimeMetrics.calories && typeof healthData.realTimeMetrics.calories === 'object') 
          ? (healthData.realTimeMetrics.calories.value || 0)
          : (healthData.realTimeMetrics.calories || 
             healthData.realTimeMetrics.caloriesActive || 
             healthData.realTimeMetrics.caloriesTotal ||
             healthData.realTimeMetrics.activeCalories ||
             healthData.realTimeMetrics.caloriesBurned || 0);
        
        const sleepRaw = (healthData.realTimeMetrics.sleep && typeof healthData.realTimeMetrics.sleep === 'object') 
          ? healthData.realTimeMetrics.sleep.value 
          : healthData.realTimeMetrics.sleep;
        const sleep = sleepRaw ? (sleepRaw > 24 ? Number((sleepRaw / 60).toFixed(1)) : sleepRaw) : 0;
        
        const heartRate = (healthData.realTimeMetrics.heartRate && typeof healthData.realTimeMetrics.heartRate === 'object') 
          ? (healthData.realTimeMetrics.heartRate.value || 0)
          : (healthData.realTimeMetrics.heartRate || 0);
        
        const stress = (healthData.realTimeMetrics.stress && typeof healthData.realTimeMetrics.stress === 'object') 
          ? (healthData.realTimeMetrics.stress.value || 0)
          : (typeof healthData.realTimeMetrics.stress === 'string' ? 0 : (healthData.realTimeMetrics.stress || 0));
        
        // Enhanced water extraction - comprehensive device paths
        const water = (healthData.realTimeMetrics.water && typeof healthData.realTimeMetrics.water === 'object') 
          ? (healthData.realTimeMetrics.water.value || (healthData.wellness?.hydration?.glasses || 0))
          : (healthData.realTimeMetrics.water || 
             (healthData.wellness?.hydration?.glasses || 0) ||
             (healthData.lifestyle?.hydration?.glasses || 0) ||
             (healthData.nutrition?.hydration?.glasses || 0) ||
             (healthData.hydration?.glasses || 0) ||
             (healthData.hydration?.value || 0));
        
        // Enhanced temperature extraction with unit normalization
        const temperature = (() => {
          let temp = healthData.vitals?.temperature?.value || 
                     healthData.vitals?.bodyTemperature?.value || 
                     healthData.realTimeMetrics?.temperature ||
                     healthData.vitalSigns?.bodyTemperature?.value ||
                     healthData.vitalSigns?.bodyTemperature ||
                     null;
          
          // Normalize temperature to Fahrenheit for consistency
          if (temp !== null && temp !== 0 && typeof temp === 'number' && !isNaN(temp)) {
            // If temperature is in Celsius range (likely 35-42°C), convert to Fahrenheit
            if (temp >= 35 && temp <= 42) {
              temp = (temp * 9/5) + 32;
            }
            // If already in Fahrenheit range (95-108°F), keep as is
            temp = Number(temp.toFixed(1));
          } else {
            temp = null; // Reset to null if not a valid number or is 0
          }
          
          return temp;
        })();
        const bloodGlucose = healthData.vitals?.bloodGlucose?.value || 
                            healthData.realTimeMetrics?.bloodGlucose || 0;
        
        data.push({
          day: dayName,
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          steps,
          calories,
          sleep,
          heartRate,
          stress,
          water,
          temperature,
          bloodGlucose,
          isToday: true,
          hasRealData: true
        });
        
        console.log('📊 REAL DATA: Today\'s live metrics:', {
          steps, calories, sleep, heartRate, stress, water, temperature, bloodGlucose
        });
      } else if (isToday && !healthData?.realTimeMetrics) {
        // TODAY but no real-time data available yet (loading state)
        data.push({
          day: dayName,
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          steps: null,
          calories: null,
          sleep: null,
          heartRate: null,
          stress: null,
          water: null,
          temperature: null,
          bloodGlucose: null,
          isToday: true,
          hasRealData: false
        });
        console.log('📊 LOADING: Today\'s data not loaded yet');
      } else {
        // HISTORICAL DAYS: Check if we have cached historical data for this date
        const dateStr = date.toISOString().split('T')[0];
        const cachedData = historicalData[dateStr];
        
        if (cachedData) {
          // Use real historical data if available
          data.push({
            day: dayName,
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            steps: cachedData.steps,
            calories: cachedData.calories,
            sleep: cachedData.sleep,
            heartRate: cachedData.heartRate,
            stress: cachedData.stress,
            water: cachedData.water,
            temperature: cachedData.temperature,
            bloodGlucose: cachedData.bloodGlucose,
            isToday: false,
            hasRealData: true
          });
          console.log(`📊 REAL HISTORICAL: Using cached data for ${dateStr}`);
        } else {
          // No historical data available for this date - show null values
          data.push({
            day: dayName,
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            steps: null,
            calories: null,
            sleep: null,
            heartRate: null,
            stress: null,
            water: null,
            temperature: null,
            bloodGlucose: null,
            isToday: false,
            hasRealData: false
          });
          console.log(`📊 NO DATA: No historical data available for ${dateStr}`);
        }
      }
    }
    
    console.log('📊 INVESTOR READY: Chart shows chronological order (left to right) with real data only');
    console.log('📊 CHART DATA:', data.map(d => ({ day: d.day, date: d.date, hasReal: d.hasRealData, steps: d.steps })));
    console.log(`📊 CHART SUMMARY: Generated ${data.length} data points for ${timeRange} (should be ${numDays})`);
    
    return data;
  }, [timeRange, healthData, historicalData]);
  
  // Memoize data generation to only recalculate when timeRange, healthData, or historicalData changes
  const data = useMemo(() => {
    // Return empty array if healthData is not loaded yet
    if (!healthData) {
      return [];
    }
    return generateTrendData();
  }, [generateTrendData, healthData, historicalData]);
  
  // Debug the chart data
  console.log('📊 HEALTH TRENDS DEBUG: Chart data for today:', data[data.length - 1]);
  console.log('📊 HEALTH TRENDS DEBUG: Full data for', timeRange, ':', data.length, 'points');
  console.log('📊 HEALTH TRENDS DEBUG: Date range:', data.length > 0 ? `${data[0]?.date} to ${data[data.length - 1]?.date}` : 'No data');
  console.log('📊 HEALTH TRENDS DEBUG: Selected category:', selectedCategory);

  // Organize metrics into logical categories with appropriate scales
  const metricCategories = {
    activity: {
      label: 'Activity & Exercise',
      icon: Activity,
      metrics: {
        steps: { 
          color: '#02276F', 
          label: 'Steps', 
          icon: Activity,
          available: dataAvailability?.steps !== false,
          format: (value) => `${value.toLocaleString()} steps`
        },
        calories: { 
          color: '#F59E0B', 
          label: 'Calories', 
          icon: Flame,
          available: true,
          format: (value) => `${value.toLocaleString()} kcal`
        }
      }
    },
    vitals: {
      label: 'Vital Signs',
      icon: Heart,
      metrics: {
        heartRate: { 
          color: '#EF4444', 
          label: 'Heart Rate', 
          icon: Heart,
          available: dataAvailability?.heartRate !== false,
          format: (value) => `${value} bpm`
        },
        temperature: { 
          color: '#F97316', 
          label: 'Temperature', 
          icon: Thermometer,
          available: dataAvailability?.temperature,
          format: (value) => `${value}°F`
        },
        bloodGlucose: { 
          color: '#EC4899', 
          label: 'Blood Glucose', 
          icon: Droplets,
          available: dataAvailability?.bloodGlucose,
          format: (value) => `${value} mg/dL`
        }
      }
    },
    wellness: {
      label: 'Sleep & Wellness',
      icon: Moon,
      metrics: {
        sleep: { 
          color: '#8B5CF6', 
          label: 'Sleep', 
          icon: Moon,
          available: dataAvailability?.sleep,
          format: (value) => `${value}h`
        },
        stress: { 
          color: '#10B981', 
          label: 'Stress Level', 
          icon: Brain,
          available: dataAvailability?.stress,
          format: (value) => `${value}%`
        },
        water: { 
          color: '#06B6D4', 
          label: 'Water', 
          icon: Droplets,
          available: true,
          format: (value) => `${value} glasses`
        }
      }
    }
  };

  const currentCategory = metricCategories[selectedCategory];
  const currentMetrics = currentCategory.metrics;

  const toggleCategory = (category) => {
    setSelectedCategory(category);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = data.find(d => d.day === label);
      return (
        <div className="p-4 bg-white border border-gray-200 shadow-lg rounded-xl">
          <p className="mb-2 font-semibold text-gray-900">
            {dataPoint?.date} {dataPoint?.isToday ? '(Today)' : ''}
          </p>
          {payload.map((entry) => {
            const metricKey = entry.dataKey;
            const metric = currentMetrics[metricKey];
            return (
              <div key={metricKey} className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600">{metric?.label}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {metric?.format ? metric.format(entry.value) : entry.value}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div>
      {/* Category Selection Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(metricCategories).map(([key, category]) => {
          const Icon = category.icon;
          const isSelected = selectedCategory === key;
          
          return (
            <button
              key={key}
              onClick={() => toggleCategory(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-blue-500 text-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{category.label}</span>
            </button>
          );
        })}
      </div>

      {/* Time Range Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">{currentCategory.label} Trends</span>
        </div>
        <select 
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-1 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="7days">Last 7 days</option>
          <option value="30days">Last 30 days</option>
          <option value="60days">Last 60 days</option>
          <option value="90days">Last 90 days</option>
        </select>
      </div>

      {/* Real Data Notice */}
      <div className="p-3 mb-4 border border-blue-200 rounded-lg bg-blue-50">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Live Health Data</span>
        </div>
        <p className="mt-1 text-xs text-blue-700">
          Displaying real metrics from your connected devices. Historical trends will populate as more data becomes available.
        </p>
      </div>

      {/* Chart */}
      <div className="p-4 border border-gray-100 h-80 bg-gradient-to-br from-gray-50 to-white rounded-xl">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              {Object.entries(currentMetrics).map(([key, metric]) => (
                <linearGradient key={`gradient-${key}`} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={metric.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={metric.color} stopOpacity={0.1}/>
                </linearGradient>
              ))}
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            
            <XAxis 
              dataKey="day" 
              stroke="#9CA3AF"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              interval={timeRange === '90days' ? 'preserveStartEnd' : timeRange === '60days' ? 'preserveStartEnd' : timeRange === '30days' ? 'preserveStartEnd' : 0}
              tick={{ fontSize: 10 }}
            />
            
            <YAxis 
              stroke="#9CA3AF"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            {Object.entries(currentMetrics).map(([key, metric], index) => {
              if (!metric.available) return null;
              
              // Use different chart types for visual variety
              const ChartComponent = index === 0 ? Area : Line;
              
              return (
                <ChartComponent
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={metric.color}
                  strokeWidth={3}
                  fill={index === 0 ? `url(#gradient-${key})` : 'none'}
                  fillOpacity={index === 0 ? 1 : 0}
                  dot={{
                    fill: metric.color,
                    stroke: metric.color,
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    r: 6,
                    stroke: metric.color,
                    strokeWidth: 2,
                    fill: '#ffffff'
                  }}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legend with current values */}
      <div className="flex flex-wrap gap-4 mt-4">
        {Object.entries(currentMetrics).map(([key, metric]) => {
          if (!metric.available) return null;
          
          const todayData = data[data.length - 1];
          const currentValue = todayData?.[key];
          
          return (
            <div key={key} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: metric.color }}
              />
              <span className="text-sm text-gray-600">{metric.label}:</span>
              <span className="text-sm font-medium text-gray-900">
                {currentValue ? metric.format(currentValue) : 'N/A'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EnhancedVitalCard = ({ title, value, unit, status, lastUpdated, trend, icon: Icon, color, available }) => {
  const colors = {
    red: 'from-red-50 to-red-100 border-red-200 text-red-600',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-600',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-600',
    orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-600',
    pink: 'from-pink-50 to-pink-100 border-pink-200 text-pink-600',
    green: 'from-green-50 to-green-100 border-green-200 text-green-600'
  };

  const statusColors = {
    normal: 'bg-green-100 text-green-800',
    stable: 'bg-green-100 text-green-800',
    abnormal: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800'
  };

  if (!available || value === null || value === undefined) {
    return (
      <div className="p-6 border border-gray-200 bg-gray-50 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="p-3 bg-gray-100 rounded-xl">
            <Icon className="w-6 h-6 text-gray-400" />
          </div>
          <span className="text-xs text-gray-400">No data</span>
        </div>
        <h4 className="mb-1 text-gray-500">{title}</h4>
        <p className="text-3xl font-bold text-gray-300">--</p>
        <p className="mt-2 text-sm text-gray-400">Awaiting device data</p>
      </div>
    );
  }

  return (
    <div className={`p-6 bg-gradient-to-br ${colors[color]} rounded-2xl border hover:shadow-lg transition-all cursor-pointer`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 bg-white rounded-xl ${colors[color].split(' ')[3]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {status && (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
            {status}
          </span>
        )}
      </div>
      
      <h4 className="mb-1 text-gray-600">{title}</h4>
      <p className="mb-1 text-3xl font-bold text-gray-900">
        {value}
        <span className="ml-1 text-lg font-normal text-gray-600">{unit}</span>
      </p>
      
      {lastUpdated && (
        <p className="mb-3 text-xs text-gray-500">
          {formatTimestamp(lastUpdated)}
        </p>
      )}
      
      {trend && (
        <div className="h-12">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend.map((v, i) => ({ value: v }))}>
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={colors[color].split(' ')[3].replace('text-', '#')} 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Compact Vital Card Component - Smaller and more informative
const CompactVitalCard = ({ title, value, unit, status, icon: Icon, color, available, deviceTypes = [] }) => {
  const colors = {
    red: 'from-red-50 to-red-100 border-red-200 text-red-600',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-600',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-600',
    orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-600',
    pink: 'from-pink-50 to-pink-100 border-pink-200 text-pink-600',
    cyan: 'from-cyan-50 to-cyan-100 border-cyan-200 text-cyan-600',
    green: 'from-green-50 to-green-100 border-green-200 text-green-600'
  };

  const statusColors = {
    normal: 'bg-green-100 text-green-700',
    stable: 'bg-green-100 text-green-700',
    abnormal: 'bg-yellow-100 text-yellow-700',
    critical: 'bg-red-100 text-red-700'
  };

  if (!available || value === null || value === undefined) {
    return (
      <div className="p-4 transition-all border border-gray-200 bg-gray-50 rounded-xl hover:bg-gray-100">
        <div className="flex items-center justify-between mb-2">
          <Icon className="w-5 h-5 text-gray-400" />
          <span className="px-2 py-1 text-xs text-gray-400 bg-gray-200 rounded-full">No data</span>
        </div>
        <h4 className="mb-1 text-sm font-medium text-gray-500">{title}</h4>
        <p className="mb-2 text-2xl font-bold text-gray-300">--</p>
        <div className="text-xs text-gray-500">
          <p className="mb-1 font-medium">Available with:</p>
          <div className="space-y-1">
            {deviceTypes.slice(0, 2).map((device, idx) => (
              <p key={idx} className="text-xs">• {device}</p>
            ))}
            {deviceTypes.length > 2 && (
              <p className="text-xs text-gray-400">+{deviceTypes.length - 2} more</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 bg-gradient-to-br ${colors[color]} rounded-xl border hover:shadow-md transition-all cursor-pointer`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${colors[color].split(' ')[2]}`} />
        {status && (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
            {status}
          </span>
        )}
      </div>
      
      <h4 className="mb-1 text-sm font-medium text-gray-700">{title}</h4>
      <p className="mb-1 text-2xl font-bold text-gray-900">
        {value}
        <span className="ml-1 text-sm font-normal text-gray-600">{unit}</span>
      </p>
      
      <div className={`w-full h-1 rounded-full ${colors[color].split(' ')[0].replace('from-', 'bg-')} opacity-30`}></div>
    </div>
  );
};

// Additional Enhanced Components

const EnhancedNutritionScore = () => {
  const nutritionData = {
    calories: { consumed: 1650, target: 2000, color: '#3B82F6' },
    protein: { consumed: 85, target: 100, color: '#10B981' },
    carbs: { consumed: 180, target: 250, color: '#F59E0B' },
    fat: { consumed: 55, target: 65, color: '#EF4444' },
    fiber: { consumed: 22, target: 30, color: '#8B5CF6' }
  };
  
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
      {Object.entries(nutritionData).map(([nutrient, values]) => (
        <div key={nutrient} className="p-4 bg-white rounded-xl">
          <p className="mb-1 text-xs text-gray-600 capitalize">{nutrient}</p>
          <p className="text-2xl font-bold text-gray-900">{values.consumed}</p>
          <p className="mb-2 text-xs text-gray-500">of {values.target}g</p>
          <div className="relative h-2 overflow-hidden bg-gray-200 rounded-full">
            <div 
              className="absolute h-full transition-all duration-500 rounded-full"
              style={{ 
                width: `${Math.min(100, (values.consumed / values.target) * 100)}%`,
                backgroundColor: values.color
              }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-600">
            {Math.round((values.consumed / values.target) * 100)}%
          </p>
        </div>
      ))}
    </div>
  );
};

const EnhancedMealTracker = ({ mealType, setMealType }) => {
  const meals = {
    breakfast: { icon: '🌅', time: '7:30 AM', calories: 420, color: 'from-yellow-100 to-orange-100' },
    lunch: { icon: '☀️', time: '12:30 PM', calories: 650, color: 'from-blue-100 to-green-100' },
    dinner: { icon: '🌙', time: '7:00 PM', calories: 580, color: 'from-purple-100 to-pink-100' },
    snacks: { icon: '🍎', time: 'Various', calories: 200, color: 'from-red-100 to-pink-100' }
  };
  
  return (
    <div>
      <div className="flex mb-6 space-x-2">
        {Object.entries(meals).map(([type, meal]) => (
          <button
            key={type}
            onClick={() => setMealType(type)}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              mealType === type 
                ? 'border-[#02276F] bg-gradient-to-br ' + meal.color + ' shadow-md' 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <span className="mb-2 text-3xl">{meal.icon}</span>
            <p className="text-sm font-semibold capitalize">{type}</p>
            <p className="text-xs text-gray-600">{meal.calories} cal</p>
            <p className="text-xs text-gray-500">{meal.time}</p>
          </button>
        ))}
      </div>
      
      <button className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#02276F] hover:bg-gray-50 transition-all group">
        <Plus className="w-6 h-6 mx-auto mb-2 text-gray-400 group-hover:text-[#02276F]" />
        <span className="text-sm text-gray-600 group-hover:text-[#02276F]">Add {mealType}</span>
      </button>
    </div>
  );
};

const HydrationTracker = ({ waterIntake }) => {
  const glasses = Array(8).fill(0).map((_, i) => i < waterIntake);
  
  return (
    <div className="p-6 border bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl border-cyan-200">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Hydration Tracker</h4>
        <Droplets className="w-6 h-6 text-cyan-600" />
      </div>
      
      <div className="grid grid-cols-4 gap-3 mb-4">
        {glasses.map((filled, idx) => (
          <div
            key={idx}
            className={`h-16 rounded-lg border-2 transition-all ${
              filled 
                ? 'bg-cyan-500 border-cyan-500' 
                : 'bg-white border-gray-300'
            }`}
          />
        ))}
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">{waterIntake} / 8 glasses</span>
        <span className="text-2xl font-bold text-cyan-600">
          {Math.round((waterIntake / 8) * 100)}%
        </span>
      </div>
    </div>
  );
};

const NutrientBreakdown = ({ healthData }) => {
  const data = [
    { name: 'Protein', value: 30, color: '#10B981' },
    { name: 'Carbs', value: 45, color: '#3B82F6' },
    { name: 'Fat', value: 25, color: '#F59E0B' }
  ];
  
  return (
    <div className="p-6 bg-white border border-gray-200 rounded-2xl">
      <h4 className="mb-4 text-lg font-semibold text-gray-900">Macro Distribution</h4>
      
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <RePieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </RePieChart>
        </ResponsiveContainer>
      </div>
      
      <div className="flex justify-center gap-4 mt-4">
        {data.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: item.color }}
            />
            <span className="text-sm text-gray-600">
              {item.name} {item.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EnhancedFoodPartners = () => {
  const partners = [
    { 
      name: 'FreshMeals Pro', 
      description: 'AI-calculated nutrition meals', 
      discount: '20% off first order', 
      logo: '🥗',
      rating: 4.8,
      color: 'from-green-50 to-emerald-50'
    },
    { 
      name: 'NutriBox', 
      description: 'Personalized meal plans', 
      discount: '15% off + Free delivery', 
      logo: '📦',
      rating: 4.6,
      color: 'from-blue-50 to-cyan-50'
    },
    { 
      name: 'HealthyBites', 
      description: 'Organic meal delivery', 
      discount: 'Get 3 meals free', 
      logo: '🌱',
      rating: 4.9,
      color: 'from-purple-50 to-pink-50'
    }
  ];
  
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {partners.map((partner, idx) => (
        <div key={idx} className={`p-5 bg-gradient-to-br ${partner.color} rounded-xl border border-gray-200 hover:shadow-md transition-all cursor-pointer`}>
          <div className="flex items-start justify-between mb-3">
            <div className="text-4xl">{partner.logo}</div>
            <div className="flex items-center gap-1 text-sm">
              <Award className="w-4 h-4 text-yellow-500" />
              <span className="font-medium">{partner.rating}</span>
            </div>
          </div>
          <h4 className="mb-1 font-semibold text-gray-900">{partner.name}</h4>
          <p className="mb-3 text-sm text-gray-600">{partner.description}</p>
          <div className="inline-block px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
            {partner.discount}
          </div>
        </div>
      ))}
    </div>
  );
};

const EnhancedHealthReports = () => {
  const reports = [
    { type: 'Complete Blood Count', date: '2024-06-15', status: 'Normal', icon: Droplets, color: 'green' },
    { type: 'ECG Report', date: '2024-06-10', status: 'Review needed', icon: Heart, color: 'yellow' },
    { type: 'Chest X-Ray', date: '2024-06-01', status: 'Clear', icon: FileText, color: 'green' },
    { type: 'HbA1c Test', date: '2024-05-20', status: 'Borderline', icon: Droplets, color: 'orange' }
  ];
  
  const statusColors = {
    'Normal': 'bg-green-100 text-green-700',
    'Clear': 'bg-green-100 text-green-700',
    'Review needed': 'bg-yellow-100 text-yellow-700',
    'Borderline': 'bg-orange-100 text-orange-700'
  };
  
  return (
    <div className="space-y-3">
      {reports.map((report, idx) => (
        <div key={idx} className="flex items-center justify-between p-4 transition-all border border-gray-200 cursor-pointer rounded-xl hover:border-blue-300 hover:shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gray-100 rounded-xl">
              <report.icon className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h5 className="font-medium text-gray-900">{report.type}</h5>
              <p className="text-sm text-gray-600">{report.date}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[report.status]}`}>
              {report.status}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      ))}
      
      <button className="w-full py-3 mt-4 font-medium text-white transition-all bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:shadow-md">
        Upload New Report
      </button>
    </div>
  );
};

// Helper functions
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
  return date.toLocaleDateString();
};

const getDefaultInsights = () => [
  {
    priority: 'high',
    title: 'Increase Daily Steps',
    message: 'You\'re 2,000 steps away from your daily goal',
    target: 'Reach 10,000 steps today',
    action: 'Start walking session'
  },
  {
    priority: 'medium',
    title: 'Hydration Reminder',
    message: 'You\'re 3 glasses behind on water intake',
    target: 'Drink 8 glasses daily',
    action: 'Set water reminders'
  },
  {
    priority: 'low',
    title: 'Sleep Quality',
    message: 'Your sleep pattern has been consistent',
    target: 'Maintain 7-8 hours nightly',
    action: 'View sleep tips'
  }
];

// Active Challenges Component
const ActiveChallenges = ({ 
  userId, 
  onNavigate, 
  userActiveChallenges = [], 
  challengeProgress = {}, 
  challengesLoading = false,
  getActiveChallengeDetails = () => [],
  getActiveChallengeNames = () => []
}) => {
  // Use the passed challenge data instead of local state
  const challengeDetails = getActiveChallengeDetails();
  
  if (challengesLoading) {
    return (
      <div className="py-8 text-center">
        <div className="w-8 h-8 mx-auto mb-2 border-b-2 border-blue-600 rounded-full animate-spin"></div>
        <p className="text-sm text-gray-600">Loading challenges...</p>
      </div>
    );
  }
  
  if (challengeDetails.length === 0) {
    return (
      <div className="py-8 text-center">
        <Target className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <h4 className="mb-2 text-lg font-semibold text-gray-900">No Active Challenges</h4>
        <p className="mb-4 text-gray-600">Start your health journey by joining a challenge!</p>
        <button 
          onClick={() => onNavigate('rewards')}
          className="px-6 py-3 font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
        >
          Browse Challenges
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Challenge Names Summary */}
      {getActiveChallengeNames().length > 0 && (
        <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
          <p className="text-sm text-purple-700">
            <strong>Active:</strong> {getActiveChallengeNames().join(', ')}
          </p>
        </div>
      )}

      {/* Challenge Cards - Similar to OverviewTab design */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {challengeDetails.map((challenge, index) => {
          // Define gradient colors based on challenge index for variety
          const gradientClasses = [
            'from-purple-500 via-purple-600 to-indigo-600',
            'from-blue-500 via-blue-600 to-cyan-600', 
            'from-green-500 via-green-600 to-emerald-600',
            'from-orange-500 via-orange-600 to-red-600',
            'from-pink-500 via-pink-600 to-rose-600',
            'from-yellow-500 via-yellow-600 to-orange-600'
          ];
          const gradientClass = gradientClasses[index % gradientClasses.length];
          
          return (
            <div
              key={challenge.id}
              className={`relative overflow-hidden px-2 py-1 text-white bg-gradient-to-br ${gradientClass} rounded-lg cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-lg group`}
              onClick={() => onNavigate('rewards')}
            >
              {/* Animated background pattern */}
              <div className="absolute inset-0 bg-black bg-opacity-10"></div>
              <div className="absolute top-0 right-0 w-6 h-6 transition-opacity transform translate-x-1 -translate-y-1 opacity-20 group-hover:opacity-30">
                <div className="w-full h-full bg-white rounded-full"></div>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <div className="flex items-center justify-center w-5 h-5 bg-white rounded-md bg-opacity-20 backdrop-blur-sm">
                      <span className="text-xs">{challenge.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold leading-none text-white truncate">{challenge.name}</h4>
                      <div className="flex items-center space-x-1 mt-0.5">
                        <span className="px-1 py-0.5 text-xs font-medium bg-white bg-opacity-20 rounded-full backdrop-blur-sm capitalize">
                          {challenge.difficulty}
                        </span>
                        <span className="px-1 py-0.5 text-xs font-medium bg-green-400 bg-opacity-90 text-green-900 rounded-full">
                          ⚡
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-4 h-4 ml-1 transition-all bg-white rounded-full bg-opacity-20 backdrop-blur-sm group-hover:bg-opacity-30">
                    <ChevronRight className="w-2.5 h-2.5 text-white group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <button 
        onClick={() => onNavigate('rewards')}
        className="w-full py-2 text-sm font-medium text-purple-600 transition-colors rounded-lg hover:text-purple-800 bg-purple-50 hover:bg-purple-100"
      >
        View All Challenges →
      </button>
    </div>
  );
};

export default LandingPage;